import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toUtcISOString } from 'common/utils/datetime';
import i18n from 'i18next';
import { localizePlaceName } from 'common/utils/localizePlaceName';
import { findKnownDestinationCenter } from 'common/api/tourApi';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { useMeetingDetail, useMeetingMembers } from 'hooks/useMeeting';
import { useUserInfo } from 'hooks/useAuth';
import { meetingStore } from 'store/meetingStore';
import { MeetingRepository } from 'repository/meetingRepository';
import { SubPlaceModal, SubPlace, SubPlaceForm, getCategoryLabel } from 'components/meeting/SubPlaceModal';
import { MeetingFinishedView } from 'components/meeting/MeetingFinishedView';
import { MeetingUpcomingView } from 'components/meeting/MeetingUpcomingView';
import { MeetingInviteSection } from 'components/meeting/MeetingInviteSection';
import { PaymentRepository } from 'repository/paymentRepository';
import { ScheduleRepository } from 'repository/scheduleRepository';
import { AttendancePrompt } from 'components/meeting/AttendancePrompt';
import { ExternalMapSheet } from 'components/map/ExternalMapSheet';
import kakaoMapIcon from 'assets/icons/map-kakao.svg';
import googleMapIcon from 'assets/icons/map-google.svg';
import { MapContainer, loadKakaoSDK } from 'components/map';
import { useUserLocation } from 'hooks/useUserLocation';
import { openShare, copyToClipboard } from 'common/utils/share';
import { LastChangeRow, ChangeHistorySheet } from 'components/meeting/ChangeHistory';
import { isNativeApp, syncNativeMeetingSchedule, removeNativeMeetingSchedule, requestApp, openNativeMapView, openNativeRealtimeLocation } from 'common/webview';
import { isInKorea } from 'common/utils/regionDetector';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { TreatCelebrationBanner } from 'components/common/TreatCelebrationBanner';
import RaceBottomSheet from 'components/meeting/RaceBottomSheet';
import ParticipantMapSheet, { PARTICIPANT_SHEET_COLLAPSED_PX } from 'components/meeting/ParticipantMapSheet';
import RaceFinishModal, { RaceFinishRow } from 'components/meeting/RaceFinishModal';
import RaceResultCard from 'components/meeting/RaceResultCard';
import { InviteFromPastMeetings } from 'components/meeting/InviteFromPastMeetings';
import PendingInvitesSection from 'components/meeting/PendingInvitesSection';
import ScheduleHistorySection from 'components/meeting/ScheduleHistorySection';
import { useBackDismiss } from 'hooks/useBackDismiss';
import MiniGamesSection from 'components/common/MiniGamesSection';
import useRealtimeMemberLocations from 'hooks/useRealtimeMemberLocations';
import { calculateEta } from 'hooks/useEta';
import { haversineMeters } from 'common/utils/geo';
import { formatPaymentLine } from 'common/utils/paymentStoreName';
import { formatPaymentAmount } from 'common/utils/treatPhrases';
import { CostInput } from 'pages/cost/CostInput';
import { IcEdit, IcShare } from 'components/common/icons/EmojiIcons';
import {
    AppScreen, Section, SectionHeader, Bleed, Badge, TimeBadge, Button, Avatar,
    InfoGrid, CopyRow, DangerZone, ActionRow, ActionList, EmptyState, InverseBanner,
    SwitchList, SwitchRow, Sheet, HeroBanner,
} from 'components/ds';
import styles from './MeetingDetail.module.css';
import { fetchPlacePhotos } from 'common/utils/placePhotos';
import { PlacePhotoPeek } from 'components/place/PlacePhotoPeek';
import { JournalSummaryCard } from 'components/journal/JournalSummaryCard';
import { useJournalSummary } from 'hooks/useMeetingJournal';
import { usePlaceReviews, feedPhotos } from 'hooks/usePlaceReviews';
import { useSafeReturnSessions } from 'hooks/useSafeReturnSessions';
import { unwrapList } from 'common/utils/apiResponse';

// 정기 모임 주기를 사람이 읽는 문구로 변환
// 예) 매주 월·수 19:00 / 매월 15일 19:00 / 매일 19:00 / 격주 월 19:00
const DOW_ORDER = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const DOW_KO: Record<string, string> = {
    SUN: '일', MON: '월', TUE: '화', WED: '수', THU: '목', FRI: '금', SAT: '토',
};
const formatRepeatSummary = (m: {
    repeatType?: string | null;
    repeatInterval?: number | null;
    repeatDaysOfWeek?: string | null;
    repeatDayOfMonth?: number | null;
    repeatTime?: string | null;
}): string => {
    const time = m.repeatTime ? ` ${m.repeatTime}` : '';
    if (m.repeatType === 'DAILY') return i18n.t('meeting.repeatDaily', { time });
    if (m.repeatType === 'WEEKLY') {
        const prefix = m.repeatInterval === 2 ? i18n.t('meeting.biweeklyPrefix') : i18n.t('meeting.weeklyPrefix');
        const wd = i18n.t('common.weekDaysShort', { returnObjects: true });
        const wdArr = Array.isArray(wd) ? (wd as string[]) : Object.values(DOW_KO);
        const days = (m.repeatDaysOfWeek || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
            .sort((a, b) => DOW_ORDER.indexOf(a) - DOW_ORDER.indexOf(b))
            .map(v => { const i = DOW_ORDER.indexOf(v); return i >= 0 ? wdArr[i] : v; })
            .join('·');
        return `${prefix}${days ? ' ' + days : ''}${time}`.trim();
    }
    if (m.repeatType === 'MONTHLY') {
        return i18n.t('meeting.repeatMonthly', { day: m.repeatDayOfMonth || 1, time });
    }
    return i18n.t('meeting.regularMeetingLabel');
};

interface Participant {
    id: number;
    name: string;
    status: 'confirmed' | 'pending' | 'declined';
    isHost: boolean;
    locationSharing: boolean;
    isMe: boolean;
}

export const MeetingDetail = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const { showAlert, showConfirm } = useAlert();
    const { meetingId: rawMeetingId } = useParams();
    const { userNo: currentUserNoFromAuth, nickname: myNickname, profileImgPath: myProfileUrl } = useUserInfo();
    const { location: userGpsLocation, fetchLocation: fetchUserGps } = useUserLocation();

    // meetingId 타입 검증 (useMemo로 메모이제이션)
    const meetingId = useMemo(() => {
        const id = typeof rawMeetingId === 'string' ? rawMeetingId : undefined;
        if (!id) {
            console.error('Invalid meetingId:', rawMeetingId, 'Type:', typeof rawMeetingId);
        }
        return id;
    }, [rawMeetingId]);

    const { data: meetingData } = useMeetingDetail();
    const { data: membersData } = useMeetingMembers();
    // activeTab state 는 탭 UI가 제거되어 더 이상 필요 없음
    const [attendanceStatus, setAttendanceStatus] = useState<'attending' | 'not_attending' | 'undecided'>('undecided');
    // 참석여부 변경 in-flight 가드 — 빠른 더블클릭으로 backend 가 같은 멤버에 대해 push 두 번 보내는 회귀 방지
    const [isUpdatingAttendance, setIsUpdatingAttendance] = useState(false);
    const [isLocationSharing, setIsLocationSharing] = useState(() => {
        // localStorage에서 위치공유 설정 불러오기
        const saved = localStorage.getItem(`locationSharing_${meetingId}`);
        return saved === 'true';
    });
    const [showShareModal, setShowShareModal] = useState(false);
    /* 변경 이력 시트 — 「마지막 변경」 줄을 누르면 열립니다 */
    const [historyOpen, setHistoryOpen] = useState(false);
    const [showInviteFromPast, setShowInviteFromPast] = useState(false);
    const [pendingReloadKey, setPendingReloadKey] = useState(0);
    // InviteFromPastMeetings 모달에서 초대 완료한 userNo 들 — MeetingInviteSection 의 행 버튼이 "초대됨" 으로 바뀌도록 전달
    const [invitedFromPastUserNos, setInvitedFromPastUserNos] = useState<number[]>([]);
    // 하단 참여자 시트 높이 — RaceBottomSheet 위치 offset 으로 사용
    const [participantSheetHeight, setParticipantSheetHeight] = useState<number>(PARTICIPANT_SHEET_COLLAPSED_PX);
    const [showMapModal, setShowMapModal] = useState(false);
    // 정산 등록/수정 모달 — 화면 이동 없이 in-place
    const [costModal, setCostModal] = useState<{ open: boolean; paymentId?: string; placeTag?: string }>({ open: false });
    // 참석여부 prompt — UNDECIDED 일 때만 노출.
    // location.state.showAttendancePrompt / ?att=1 같은 외부 트리거는 "보여달라"는 hint 일 뿐이고
    // 실제 노출 가드는 membersData 로드 후 status 가 UNDECIDED 인지로 결정한다 (이미 참석/불참 선택한
    // 사용자에게 또 묻지 않도록). 아래 useEffect 에서 setShowAttendPrompt(true) 발사.
    const [showAttendPrompt, setShowAttendPrompt] = useState(false);
    const [showExternalMap, setShowExternalMap] = useState(false);

    // 서브 장소 관련 상태
    const [subPlaces, setSubPlaces] = useState<SubPlace[]>([]);
    // 정산 — 모임의 지출 list (정산 카드 / 송금 매트릭스에서 사용)
    const [payments, setPayments] = useState<any[]>([]);
    // 모임 정보 카드에서 지도에 표시할 장소 ID (기본: 현재시간 기준 가장 가까운 장소)
    const [selectedPlaceKey, setSelectedPlaceKey] = useState<string | null>(null);
    // 지도 provider(kakao/google) flicker 방지 — 30초 refetch 도중 순간적 데이터 미완성으로
    // activeKey 가 primary <-> sub_place 오락가락하는 것을 이전 유효값으로 안정화.
    const stableActiveKeyRef = useRef<string | null>(null);
    // 장소 칩을 고르면 이 자리로 스크롤합니다 (예전엔 querySelector 로 세 군데서 각각 찾았습니다)
    const mapRef = useRef<HTMLDivElement>(null);
    const [showSubPlaceModal, setShowSubPlaceModal] = useState(false);
    const [editingSubPlace, setEditingSubPlace] = useState<SubPlace | null>(null);
    const [subPlaceForm, setSubPlaceForm] = useState<SubPlaceForm | undefined>(undefined);
    const [isCustomVisitTime, setIsCustomVisitTime] = useState(false);
    const [locationStateProcessed, setLocationStateProcessed] = useState(false);

    // 장소 정보 (Google Places - 사진/별점)
    const [placeInfo, setPlaceInfo] = useState<{
        photoUrl?: string;
        rating?: number;
        userRatingsTotal?: number;
        openNow?: boolean;
        googlePlaceId?: string;
    } | null>(null);

    /* 상단 배너 사진.
       place-info(이름+좌표로 구글 조회) 는 국내 장소에서 빈 응답이 잦아,
       저장된 외부 장소 ID 로 travel/photos 를 한 번 더 봅니다.
       홈 카드가 사진을 얻는 경로와 같습니다. */
    const [heroPhotos, setHeroPhotos] = useState<string[]>([]);

    // 컴포넌트 마운트 시 스크롤 위치 초기화
    useEffect(() => {
        // ds 의 Screen 은 window 스크롤을 씁니다 (별도 스크롤 컨테이너 없음)
        window.scrollTo(0, 0);
    }, [meetingId]);

    // 컴포넌트 마운트 시 모임 상세 및 멤버 정보 조회
    useEffect(() => {
        if (meetingId) {
            meetingStore.onGetMeetingDetail(meetingId);
            meetingStore.onGetMeetingMembers(meetingId);
        }
    }, [meetingId]);

    // 자동 갱신 — 탭 복귀/포커스/가시성 변경 + 30초 폴링으로 멤버 합류 실시간 반영
    useEffect(() => {
        if (!meetingId) return;
        const refresh = () => {
            meetingStore.onGetMeetingMembers(meetingId);
            meetingStore.onGetMeetingDetail(meetingId);
            setPendingReloadKey(k => k + 1);
        };
        const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
        const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) refresh(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', refresh);
        window.addEventListener('pageshow', onPageShow);
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') refresh();
        }, 30_000);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', refresh);
            window.removeEventListener('pageshow', onPageShow);
            clearInterval(intervalId);
        };
    }, [meetingId]);

    // "내가 쏜다" 내역 조회 (모임별)
    const [treats, setTreats] = useState<any[]>([]);
    useEffect(() => {
        if (!meetingId) return;
        instance
            .get(`${getApiUrl('meetings')}/${meetingId}/payments/treats`)
            .then(res => {
                const list = res?.data?.data || res?.data || [];
                setTreats(Array.isArray(list) ? list : []);
            })
            .catch(() => { /* 무시 */ });
    }, [meetingId]);

    // 안전 귀가 활성 세션 조회 (이 모임 기준)
    const safeReturnSessions = useSafeReturnSessions(meetingId, pendingReloadKey);

    // PlaceSearch 에서 예정 모임 장소 변경하고 복귀했을 때 — placeInfo 자동 적용
    useEffect(() => {
        if (!meetingId) return;
        const st: any = location.state;
        if (!st?.upcomingPlaceUpdate || !st?.placeInfo) return;
        const pi = st.placeInfo;
        if (!pi.name) return;
        // 중복 실행 방지 — state 비우고 호출
        navigate(location.pathname, { replace: true, state: {} });
        (async () => {
            try {
                await MeetingRepository.onPutMeetingPlace(meetingId, {
                    placeName: pi.name,
                    placeAddress: pi.address || '',
                    latitude: pi.lat != null ? Number(pi.lat) : undefined,
                    longitude: pi.lng != null ? Number(pi.lng) : undefined,
                });
                meetingStore.onGetMeetingDetail(meetingId);
                showAlert({ message: t('meeting.placeSet') });
            } catch {
                showAlert({ message: t('meeting.placeSetFail') });
            }
        })();
    }, [location.state, meetingId, location.pathname, navigate, showAlert]);

    // 지출 등록 후 모임 상세 복귀 — 정산 카드로 스크롤
    const settlementScrollDone = useRef(false);
    useEffect(() => {
        if (settlementScrollDone.current) return;
        if (!(location.state as any)?.scrollToSettlement) return;
        settlementScrollDone.current = true;
        const timer = setTimeout(() => {
            document.getElementById('settlement-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            try { navigate(location.pathname, { replace: true, state: {} }); } catch { /* ignore */ }
        }, 600);
        return () => clearTimeout(timer);
    }, [location.state, location.pathname, navigate]);

    // 미정산 리마인더 푸시 탭 → 자동 "보냈어요" 처리
    // 쿼리 ?payment=mark-sent&splitId=... 가 있으면 해당 paymentId 조회 후 markSent API 호출
    useEffect(() => {
        const search = location.search;
        if (!search || !meetingId) return;
        const params = new URLSearchParams(search);
        if (params.get('payment') !== 'mark-sent') return;
        const splitId = params.get('splitId');
        if (!splitId) return;

        // 쿼리 제거 — 중복 실행 방지
        navigate(location.pathname, { replace: true });

        (async () => {
            try {
                // splitId → paymentId 찾기 위해 모임 정산 목록 로드
                const res = await instance.get(`${getApiUrl('payments')}/${meetingId}/payments`);
                const list: any[] = res?.data?.data || res?.data || [];
                const target = list.find(p => (p.splits || []).some((s: any) => String(s.splitId) === splitId));
                if (!target) {
                    showAlert({ message: t('cost.markSentNotFound') });
                    return;
                }
                const resp = await instance.post(
                    `${getApiUrl('paymentComplete')}/${target.paymentId}/sent`
                );
                if (resp?.status >= 200 && resp?.status < 300) {
                    showAlert({ message: t('cost.markSentDone') });
                }
            } catch {
                showAlert({ message: t('cost.markSentFail') });
            }
        })();
    }, [location.search, meetingId, navigate, location.pathname, showAlert, t]);

    // API 데이터 변환
    const meeting = useMemo(() => {
        if (!meetingData?.data) {
            return {
                id: meetingId,
                name: t('meeting.loadingText'),
                date: '',
                time: '',
                location: '',
                locationDetail: '',
                description: '',
                status: 'upcoming' as const,
                inviteCode: '',
                createdBy: '',
                isRegularMeeting: false,
                repeatType: null as string | null,
                repeatInterval: null as number | null,
                repeatDaysOfWeek: null as string | null,
                repeatDayOfMonth: null as number | null,
                repeatTime: null as string | null,
                rolloverPendingAt: null as string | null,
            };
        }

        const data = meetingData.data;

        // 스케줄 정보 디버깅
        console.log('MeetingDetail 스케줄 정보:', {
            hasSchedule: !!data.schedule,
            schedule: data.schedule,
            startDatetime: data.schedule?.startDatetime
        });

        return {
            id: data.meetingId,
            name: data.meetingName,
            date: data.schedule?.startDatetime ?
                new Date(data.schedule.startDatetime).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' }) :
                t('home.scheduleNotSet'),
            time: data.schedule?.startDatetime ?
                new Date(data.schedule.startDatetime).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) :
                '',
            location: data.placeName || t('meeting.placeNotSet'),
            locationDetail: data.placeAddress || '',
            description: data.description || '',
            status: data.status?.toLowerCase() || 'upcoming',
            inviteCode: data.inviteCode || '',
            createdBy: data.creatorNickname ? decodeURIComponent(data.creatorNickname) : t('meeting.unknownUser'),
            isRegularMeeting: data.isRegularMeeting || false,
            repeatType: data.repeatType || null,
            repeatInterval: data.repeatInterval || null,
            repeatDaysOfWeek: data.repeatDaysOfWeek || null,
            repeatDayOfMonth: data.repeatDayOfMonth || null,
            repeatTime: data.repeatTime || null,
            rolloverPendingAt: data.rolloverPendingAt || null,
            // 여행 관련 필드
            meetingType: data.meetingType || 'DAY',
            travelDestination: data.travelDestination || '',
            travelType: data.travelType || '',
            travelStartDate: data.travelStartDate ? new Date(data.travelStartDate) : null,
            travelEndDate: data.travelEndDate ? new Date(data.travelEndDate) : null,
            meetingTime: data.meetingTime || '',
            hotelPlaceId: data.hotelPlaceId || '',
            hotelName: data.hotelName || '',
            hotelAddress: data.hotelAddress || '',
            latitude: data.latitude ? parseFloat(data.latitude) : 0,
            longitude: data.longitude ? parseFloat(data.longitude) : 0,
            /* 장소 사진·후기를 붙이는 열쇠.
               내부 place ID 를 씁니다. 이름+주소로 중복 제거되어 같은 가게가 한 행을
               쓰고, 한 번 정해지면 바뀌지 않습니다.
               외부 provider ID 는 장소를 고른 경로에 따라 비어 있다가 나중에 채워지기도
               해서(updateExternalPlaceId) 키로 삼으면 그 시점에 후기가 갈립니다. */
            externalPlaceId: data.externalPlaceId || '',
            placeKey: data.placeId || data.externalPlaceId || '',
            // 마지막 수정자 정보 (주최자가 아닌 멤버 수정 시 표시)
            lastModifiedBy: data.lastModifiedBy || null,
            lastModifiedByNickname: data.lastModifiedByNickname ? (() => { try { return decodeURIComponent(data.lastModifiedByNickname); } catch { return data.lastModifiedByNickname; } })() : '',
            lastModifiedAt: data.lastModifiedAt || null,
            /* 마지막 「장소·시간」 변경. lastModifiedBy 와 다릅니다 — 그 값은 모임
               이름만 고쳐도 찍혀서 장소·시간이 바뀐 것처럼 보입니다. */
            lastChangedBy: data.lastChangedBy || null,
            lastChangedByNickname: data.lastChangedByNickname ? (() => { try { return decodeURIComponent(data.lastChangedByNickname); } catch { return data.lastChangedByNickname; } })() : '',
            lastChangedAt: data.lastChangedAt || null,
            lastChangedTargetType: data.lastChangedTargetType || null,
            lastChangedAction: data.lastChangedAction || null,
            lastChangedTargetLabel: data.lastChangedTargetLabel || null,
            creatorNo: data.creatorNo || null,
            // 도착 레이스
            raceEnabled: Boolean(data.raceEnabled),
            lateFeeAmount: Number(data.lateFeeAmount || 0),
            shuttlePenaltyEnabled: data.shuttlePenaltyEnabled !== false, // 기본 true
            raceFinalizedAt: data.raceFinalizedAt || null,
            // 정산 요약 (지난 모임 상단 Hero 표시용)
            hasUnsettledPayment: data.hasUnsettledPayment || false,
            totalPaymentAmount: data.totalPaymentAmount || 0,
            myBalanceAmount: data.myBalanceAmount || 0,
            myUnsettledAmount: data.myUnsettledAmount || 0,
            scheduleStartDatetime: data.schedule?.startDatetime || null,
            scheduleEndDatetime: data.schedule?.endDatetime || null,
            confirmedScheduleId: data.confirmedScheduleId || data.schedule?.scheduleId || null,
            endedAt: data.endedAt || null, // 주최자 즉시 종료 시각 (NULL = 자동 종료)
            paymentSettledAt: data.paymentSettledAt || null, // 정산 완료 시각 (NULL = 미완료)
            // 일정 투표 진행 중 (투표 Hero 카드 표시용)
            hasActiveProposal: data.hasActiveProposal || false,
            proposalVoteCount: data.proposalVoteCount || 0,
        };
    }, [meetingData, meetingId]);

    // 지난 모임 여부 (종료 시각 기준)
    const isPastMeeting = useMemo(() => {
        const end = (meeting as any).scheduleEndDatetime;
        if (!end) return false;
        return new Date(end).getTime() < Date.now();
    }, [meeting]);

    // 여행 모임인지 확인
    const isTravelMeeting = meeting.meetingType === 'TRAVEL';

    // 여행지 좌표 — 호텔 sub-place 검색 시 anchor 로 사용 (SubPlaceModal → PlaceSearch 흐름)
    // 1) meeting 응답의 latitude/longitude 가 여행지일 수 있으면 그대로
    // 2) 없으면 travelDestination 문자열을 KR_POPULAR_CITIES/JP 로컬 매핑에서 lookup (휴양지 중심)
    const travelDestinationCenter = useMemo(() => {
        if (!isTravelMeeting) return null;
        const known = findKnownDestinationCenter(meeting.travelDestination || '');
        if (known) return known;
        if (meeting.latitude && meeting.longitude) {
            return { lat: Number(meeting.latitude), lng: Number(meeting.longitude) };
        }
        return null;
    }, [isTravelMeeting, meeting.travelDestination, meeting.latitude, meeting.longitude]);

    // 여행 모임이면 귀가 도우미 노출 판정(귀국 확인)을 위해 현재 위치 조회
    useEffect(() => {
        if (isTravelMeeting) fetchUserGps();
    }, [isTravelMeeting, fetchUserGps]);

    // 집결지 좌표 fallback — meeting.lat/lng 가 비어있을 때
    // 1) meeting.location 텍스트를 카카오 geocode 로 변환
    // 2) 그것도 실패하면 meeting.travelDestination 으로 재시도 (여행 모임 케이스)
    const [geocodedPrimaryCenter, setGeocodedPrimaryCenter] = useState<{ lat: number; lng: number } | null>(null);
    useEffect(() => {
        setGeocodedPrimaryCenter(null);
        if (meeting.latitude && meeting.longitude) return; // 좌표 이미 있으면 skip

        const candidates: string[] = [];
        if (meeting.location) candidates.push(meeting.location.trim());
        if (meeting.travelDestination && meeting.travelDestination !== meeting.location) {
            candidates.push(meeting.travelDestination.trim());
        }
        const queries = candidates.filter(Boolean);
        if (queries.length === 0) return;

        let cancelled = false;
        const tryQueries = async () => {
            try {
                await loadKakaoSDK();
                if (cancelled || !window.kakao?.maps?.services) {
                    console.warn('[MeetingDetail] kakao SDK services 미로드 — geocode fallback 불가');
                    return;
                }
                const kakao = window.kakao as any;
                const geocoder = new kakao.maps.services.Geocoder();
                const places = new kakao.maps.services.Places();

                const tryOne = (query: string): Promise<{ lat: number; lng: number } | null> => {
                    return new Promise((resolve) => {
                        geocoder.addressSearch(query, (results: any[], status: string) => {
                            if (cancelled) return resolve(null);
                            if (status === kakao.maps.services.Status.OK && results.length > 0) {
                                return resolve({ lat: parseFloat(results[0].y), lng: parseFloat(results[0].x) });
                            }
                            places.keywordSearch(query, (kwResults: any[], kwStatus: string) => {
                                if (cancelled) return resolve(null);
                                if (kwStatus === kakao.maps.services.Status.OK && kwResults.length > 0) {
                                    return resolve({ lat: parseFloat(kwResults[0].y), lng: parseFloat(kwResults[0].x) });
                                }
                                resolve(null);
                            });
                        });
                    });
                };

                for (const q of queries) {
                    const found = await tryOne(q);
                    if (cancelled) return;
                    if (found) {
                        console.log('[MeetingDetail] geocode 성공:', q, found);
                        setGeocodedPrimaryCenter(found);
                        return;
                    }
                }
                console.warn('[MeetingDetail] geocode 모두 실패:', queries);
            } catch (e) {
                console.warn('[MeetingDetail] geocode 예외:', e);
            }
        };

        tryQueries();
        return () => { cancelled = true; };
    }, [meeting.location, meeting.latitude, meeting.longitude, meeting.travelDestination]);

    // 여행 기간 계산
    const travelPeriod = useMemo(() => {
        if (!meeting.travelStartDate || !meeting.travelEndDate) return null;
        const start = meeting.travelStartDate;
        const end = meeting.travelEndDate;
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const nights = days - 1;
        return { days, nights, start, end };
    }, [meeting.travelStartDate, meeting.travelEndDate]);

    // 숙박지(HOTEL) 목록 — 체크인 일자 내림차순 → 같은 일자 내 createdAt 내림차순
    const hotelPlaces = useMemo(() => {
        const onlyHotels = (subPlaces || []).filter(sp => sp.category === 'HOTEL');
        return onlyHotels.sort((a: any, b: any) => {
            const aIn = (a.startDatetime || '').slice(0, 10);
            const bIn = (b.startDatetime || '').slice(0, 10);
            if (aIn !== bIn) return bIn.localeCompare(aIn);
            const aC = a.createdAt || '';
            const bC = b.createdAt || '';
            return bC.localeCompare(aC);
        });
    }, [subPlaces]);

    // 호텔 좌표 후보 — finalize 시 본인 위치 보정에 사용 (sub_place hotel + legacy hotelLatitude/Longitude)
    const hotelCandidatesForFinalize = useMemo(() => {
        const list: Array<{ lat: number; lng: number; name: string }> = [];
        (hotelPlaces || []).forEach((h: any) => {
            if (h.latitude && h.longitude) {
                list.push({ lat: Number(h.latitude), lng: Number(h.longitude), name: h.placeName || '숙소' });
            }
        });
        const legacyLat = meetingData?.data?.hotelLatitude;
        const legacyLng = meetingData?.data?.hotelLongitude;
        if (legacyLat && legacyLng) {
            list.push({ lat: parseFloat(legacyLat), lng: parseFloat(legacyLng), name: meetingData?.data?.hotelName || '숙소' });
        }
        return list;
    }, [hotelPlaces, meetingData]);

    // 여행일정 목록 — HOTEL 제외, 해당 일자 내에서 최신순
    const itinerarySubPlaces = useMemo(() => {
        return (subPlaces || []).filter(sp => sp.category !== 'HOTEL');
    }, [subPlaces]);

    // 장소 정보 (사진/별점) 조회
    useEffect(() => {
        if (!meeting.location || meeting.location === t('meeting.placeNotSet') || !meeting.latitude || !meeting.longitude) return;
        if (meeting.latitude === 0 && meeting.longitude === 0) return;

        const fetchPlaceInfo = async () => {
            try {
                const res = await instance.get(getApiUrl('placeInfo'), {
                    params: { name: meeting.location, lat: meeting.latitude, lng: meeting.longitude }
                });
                const data = res?.data?.data;
                if (data && Object.keys(data).length > 0) {
                    setPlaceInfo(data);
                }
            } catch (err) {
                // 장소 정보 없어도 무시
            }
        };
        fetchPlaceInfo();
    }, [meeting.location, meeting.latitude, meeting.longitude]);

    useEffect(() => {
        const externalId = (meetingData?.data as any)?.externalPlaceId;
        if (!externalId) { setHeroPhotos([]); return; }
        let alive = true;
        (async () => {
            try {
                const photos = await fetchPlacePhotos([externalId]);
                const pd = photos[externalId];
                if (!alive || !pd) return;
                // 대표 사진을 앞에 두고 중복은 걸러 냅니다
                setHeroPhotos(Array.from(new Set([pd.photoUrl, ...(pd.photos || [])].filter(Boolean) as string[])));
            } catch { /* 사진은 없어도 배너가 성립합니다 */ }
        })();
        return () => { alive = false; };
    }, [meetingData]);

    // 한국 장소인지 여부
    const isKoreaPlace = meeting.latitude && meeting.longitude
        ? isInKorea(meeting.latitude, meeting.longitude)
        : true;

    // 서브 장소 목록 조회
    const fetchSubPlaces = useCallback(async () => {
        if (!meetingId) return;
        try {
            setSubPlaces(unwrapList(await MeetingRepository.onGetSubPlaces(meetingId)));
        } catch (error) {
            console.error('서브 장소 조회 실패:', error);
            setSubPlaces([]);
        }
    }, [meetingId]);

    // 모임 상세 조회 후 서브 장소 조회
    useEffect(() => {
        if (meetingId && meetingData?.data) {
            fetchSubPlaces();
        }
    }, [meetingId, meetingData, fetchSubPlaces]);

    // 정산 지출 목록 조회 — 모임 상세의 정산 카드 + 완료 모임의 송금 매트릭스에서 사용
    const fetchPayments = useCallback(async () => {
        if (!meetingId) return;
        try {
            const res: any = await PaymentRepository.onGetPayments(meetingId);
            const list = res?.data?.data ?? res?.data ?? [];
            setPayments(Array.isArray(list) ? list : []);
        } catch (e) {
            console.warn('[MeetingDetail] 지출 목록 조회 실패:', e);
            setPayments([]);
        }
    }, [meetingId]);

    useEffect(() => {
        if (meetingId && meetingData?.data) {
            fetchPayments();
        }
    }, [meetingId, meetingData, fetchPayments]);

    // 장소 태그 파싱 — storeName 인코딩에서 장소 태그(main/sub:N/etc)를 결정.
    // 신규 포맷: [main] / [sub:{id}] / [etc]
    // 구버전 포맷 호환: [1차]→main, [2차]→첫 sub_place, [3차]→둘째 sub_place, [기타]→etc
    const getPlaceTagForPayment = useCallback((storeName: string | undefined, sortedSubPlaces: any[]): string => {
        if (!storeName) return 'main';
        const m = storeName.match(/^\[([^\]]+)\]/);
        if (!m) return 'main';
        const tag = m[1];
        if (tag === 'main' || tag === 'etc') return tag;
        if (tag.startsWith('sub:')) return tag;
        // 구버전 라벨 매핑
        if (tag === '1차') return 'main';
        if (tag === '기타') return 'etc';
        if (tag === '2차' && sortedSubPlaces[0]) return `sub:${sortedSubPlaces[0].subPlaceId}`;
        if (tag === '3차' && sortedSubPlaces[1]) return `sub:${sortedSubPlaces[1].subPlaceId}`;
        return 'etc'; // 알 수 없는 태그 — 기타로 fall back
    }, []);

    // 장소별 payment 그룹 — key: 'main' | `sub:{id}` | 'etc'
    const paymentsByPlace = useMemo(() => {
        const sortedNonHotel = (subPlaces || [])
            .filter((sp: any) => sp.category !== 'HOTEL')
            .slice()
            .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));
        const groups: Record<string, any[]> = { main: [], etc: [] };
        sortedNonHotel.forEach(sp => { groups[`sub:${sp.subPlaceId}`] = []; });
        (payments || []).filter((p: any) => p.status !== 'DELETED').forEach((p: any) => {
            const key = getPlaceTagForPayment(p.storeName, sortedNonHotel);
            if (groups[key]) groups[key].push(p);
            else groups.etc.push(p); // sub:{id} 인데 sub_place 가 삭제된 경우
        });
        return groups;
    }, [payments, subPlaces, getPlaceTagForPayment]);

    const computeRoundSummary = useCallback((placeKey: string) => {
        const list = paymentsByPlace[placeKey] || [];
        let total = 0, myPaid = 0, mySplit = 0;
        const myUserNo = Number(currentUserNoFromAuth) || 0;
        for (const p of list) {
            total += Number(p.totalAmount || 0);
            if (myUserNo > 0 && Number(p.paidByNo) === myUserNo && !p.isTreat) {
                myPaid += Number(p.totalAmount || 0);
            }
            for (const s of (p.splits || [])) {
                if (myUserNo > 0 && Number(s.userNo) === myUserNo) {
                    mySplit += Number(s.amount || 0);
                }
            }
        }
        return { list, total, count: list.length, myBalance: myPaid - mySplit };
    }, [paymentsByPlace, currentUserNoFromAuth]);

    // sub_place 좌표 백필 — 백엔드에 좌표 없이 저장된 기존 sub_place 들을
    // placeAddress / placeName 으로 카카오 geocode 하여 lat/lng 채움. 화면 표시 전용 (서버 update 는 안 함).
    useEffect(() => {
        if (!subPlaces || subPlaces.length === 0) return;
        const targets = subPlaces.filter(sp => (!sp.latitude || !sp.longitude) && (sp.placeAddress || sp.placeName));
        if (targets.length === 0) return;

        let cancelled = false;
        (async () => {
            try {
                await loadKakaoSDK();
                if (cancelled || !window.kakao?.maps?.services) return;
                const kakao = window.kakao as any;
                const geocoder = new kakao.maps.services.Geocoder();
                const places = new kakao.maps.services.Places();
                const tryOne = (q: string): Promise<{ lat: number; lng: number } | null> => new Promise((resolve) => {
                    geocoder.addressSearch(q, (r: any[], s: string) => {
                        if (cancelled) return resolve(null);
                        if (s === kakao.maps.services.Status.OK && r.length > 0) {
                            return resolve({ lat: parseFloat(r[0].y), lng: parseFloat(r[0].x) });
                        }
                        places.keywordSearch(q, (r2: any[], s2: string) => {
                            if (cancelled) return resolve(null);
                            if (s2 === kakao.maps.services.Status.OK && r2.length > 0) {
                                return resolve({ lat: parseFloat(r2[0].y), lng: parseFloat(r2[0].x) });
                            }
                            resolve(null);
                        });
                    });
                });

                const updates = new Map<number, { lat: number; lng: number }>();
                for (const sp of targets) {
                    const q = (sp.placeAddress || sp.placeName || '').trim();
                    if (!q) continue;
                    const found = await tryOne(q);
                    if (cancelled) return;
                    if (found) updates.set(sp.subPlaceId, found);
                }
                if (updates.size > 0 && !cancelled) {
                    setSubPlaces(prev => prev.map(sp => updates.has(sp.subPlaceId)
                        ? { ...sp, latitude: updates.get(sp.subPlaceId)!.lat, longitude: updates.get(sp.subPlaceId)!.lng }
                        : sp));
                }
            } catch (e) {
                console.warn('[MeetingDetail] sub_place geocode 백필 실패:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [subPlaces]);

    // TimePicker 또는 PlaceSearch에서 돌아온 경우 처리 (한 번만 실행)
    // editingSubPlaceId 가 있는데 subPlaces 아직 fetched 전이면 wait — 안 그러면 setEditingSubPlace 누락 → update 가 create 로 잘못 처리됨
    useEffect(() => {
        if (!location.state?.fromSubPlaceModal || locationStateProcessed) return;
        if (location.state.editingSubPlaceId && (!subPlaces || subPlaces.length === 0)) return;

        const savedForm = location.state.subPlaceForm;
        const savedIsCustomTime = location.state.isCustomVisitTime || false;

        // TimePicker에서 돌아온 경우
        if (location.state.selectedTime && savedForm) {
            setSubPlaceForm({
                ...savedForm,
                visitTime: location.state.selectedTime
            });
            setIsCustomVisitTime(true);
        }
        // PlaceSearch에서 돌아온 경우 — 좌표(lat/lng) 도 form 에 함께 저장 (sub_place 등록 시 백엔드에 전달)
        else if (location.state.placeInfo) {
            const placeInfo = location.state.placeInfo;
            const defaultForm = { placeName: '', placeAddress: '', category: isTravelMeeting ? 'ATTRACTION' : 'PRIMARY', dayNumber: 1, visitTime: '', description: '' };
            const latNum = typeof placeInfo.lat === 'number' ? placeInfo.lat : (placeInfo.lat ? parseFloat(placeInfo.lat) : undefined);
            const lngNum = typeof placeInfo.lng === 'number' ? placeInfo.lng : (placeInfo.lng ? parseFloat(placeInfo.lng) : undefined);
            setSubPlaceForm({
                ...(savedForm || defaultForm),
                placeName: placeInfo.name || savedForm?.placeName || '',
                placeAddress: placeInfo.address || savedForm?.placeAddress || '',
                ...(typeof latNum === 'number' && !isNaN(latNum) ? { latitude: latNum } : {}),
                ...(typeof lngNum === 'number' && !isNaN(lngNum) ? { longitude: lngNum } : {}),
                ...(placeInfo.externalPlaceId || placeInfo.placeId ? { placeId: placeInfo.externalPlaceId || placeInfo.placeId } : {}),
            });
            setIsCustomVisitTime(savedIsCustomTime);
        }
        // 기타 (폼 데이터만 복원)
        else if (savedForm) {
            setSubPlaceForm(savedForm);
            setIsCustomVisitTime(savedIsCustomTime);
        }

        // 수정 중이던 서브 장소가 있으면 복원
        if (location.state.editingSubPlaceId) {
            const editingPlace = subPlaces.find(sp => sp.subPlaceId === location.state.editingSubPlaceId);
            if (editingPlace) {
                setEditingSubPlace(editingPlace);
            }
        }

        setShowSubPlaceModal(true);
        setLocationStateProcessed(true);
    }, [location.state, subPlaces, isTravelMeeting, locationStateProcessed]);

    const participants: Participant[] = useMemo(() => {
        const myUserNo = currentUserNoFromAuth || Number(sessionStorage.getItem('userNo')) || 0;
        return membersData?.data?.map((member: any) => {
            const rawNickname = member.nickname || member.nickName || member.userName || '';
            let name = t('home.userFallback');
            if (rawNickname) {
                try {
                    name = decodeURIComponent(rawNickname);
                } catch {
                    name = rawNickname;
                }
            }
            const uNo = member.userNo || member.user_no;
            return {
                id: uNo,
                name: name || String(uNo || t('home.userFallback')),
                status: member.attendanceStatus === 'ATTENDING'
                    ? ('confirmed' as const)
                    : member.attendanceStatus === 'NOT_ATTENDING'
                        ? ('declined' as const)
                        : ('pending' as const),
                isHost: member.role === 'CREATOR' || member.role === 'HOST',
                locationSharing: member.locationSharing === true,
                isMe: myUserNo > 0 && Number(uNo) === Number(myUserNo),
            };
        }) || [];
    }, [membersData, currentUserNoFromAuth]);

    // CREATOR 여부 — 수정 버튼 노출 권한 체크 (race 훅보다 먼저 선언 필요)
    const isCreator = useMemo(() => {
        const storedUserNo = sessionStorage.getItem('userNo');
        const currentUserNo = currentUserNoFromAuth || (storedUserNo ? Number(storedUserNo) : null);
        if (!currentUserNo) return false;
        const creatorNo = (meeting as any)?.creatorNo;
        if (creatorNo && Number(creatorNo) === Number(currentUserNo)) return true;
        if (!membersData?.data) return false;
        const me = membersData.data.find((m: any) => Number(m.userNo) === Number(currentUserNo));
        return me?.role === 'CREATOR' || me?.role === 'HOST';
    }, [meeting, membersData, currentUserNoFromAuth]);

    // 참여 여부 — 푸시/링크로 들어온 비멤버에게 '참여하기' CTA 노출용.
    // 게스트(비회원)는 JWT/userNo 가 없고 guestUserNo(음수) 로 meeting_member 에 등록되므로 함께 검사.
    const isMember = useMemo(() => {
        const storedUserNo = sessionStorage.getItem('userNo');
        const guestUserNo = sessionStorage.getItem('guestUserNo');
        const currentUserNo = currentUserNoFromAuth
            || (storedUserNo ? Number(storedUserNo) : null)
            || (guestUserNo ? Number(guestUserNo) : null);
        if (!currentUserNo || !membersData?.data) return false;
        return membersData.data.some((m: any) => Number(m.userNo) === Number(currentUserNo));
    }, [membersData, currentUserNoFromAuth]);
    const [isJoining, setIsJoining] = useState(false);
    const handleJoinMeeting = async () => {
        if (!meetingId || isJoining) return;
        setIsJoining(true);
        try {
            await MeetingRepository.onPostJoinMeeting(meetingId);
            // refetch await 처리 — 이게 없으면 race: 이 refetch 가 늦게 완료되어
            //   AttendancePrompt 의 PUT 결과 (locationSharing=true) 를 stale 데이터(false)로 덮어쓰던 회귀.
            await meetingStore.onGetMeetingMembers(meetingId);
            await meetingStore.onGetMeetingDetail(meetingId);
            showAlert({ message: t('meeting.joined') });
            setShowAttendPrompt(true);
        } catch {
            showAlert({ message: t('meeting.joinFail') });
        } finally {
            setIsJoining(false);
        }
    };

    // 도착 레이스 — 시작 30분 전 ~ 시작 이후 15분까지 활성
    const raceEnabled = Boolean((meeting as any)?.raceEnabled);
    const meetingStartMs = useMemo(() => {
        const s = meetingData?.data?.schedule?.startDatetime;
        return s ? new Date(s).getTime() : null;
    }, [meetingData?.data?.schedule?.startDatetime]);

    const isRaceWindow = useMemo(() => {
        if (!raceEnabled || !meetingStartMs) return false;
        const now = Date.now();
        const minutesToStart = (meetingStartMs - now) / 60000;
        return minutesToStart <= 30 && minutesToStart >= -15;
    }, [raceEnabled, meetingStartMs]);

    // GPS 마커 노출 조건 — 모임 시작 ±2시간 이내 또는 race window 안에서만.
    // 한참 뒤(예: 1월 4일) 예정 모임에서 내 현재 위치를 지도에 띄우는 건 사생활/UX 모두 의미 없음.
    const showMyGpsMarker = useMemo(() => {
        if (isRaceWindow) return true;
        if (!meetingStartMs) return false;
        const diffMs = Math.abs(meetingStartMs - Date.now());
        return diffMs <= 2 * 60 * 60 * 1000;
    }, [isRaceWindow, meetingStartMs]);

    // 측위는 위 조건 만족 시에만 (브라우저 권한 prompt 도 그때 발생)
    useEffect(() => {
        if (showMyGpsMarker) fetchUserGps();
    }, [showMyGpsMarker]); // eslint-disable-line react-hooks/exhaustive-deps

    // 1분마다 tick (재계산 트리거)
    const [raceTick, setRaceTick] = useState(0);
    useEffect(() => {
        if (!isRaceWindow) return;
        const id = setInterval(() => setRaceTick(v => v + 1), 15000);
        return () => clearInterval(id);
    }, [isRaceWindow]);

    const memberLocations = useRealtimeMemberLocations(
        isRaceWindow ? (meetingId || null) : null,
        currentUserNoFromAuth || null,
        isRaceWindow,
        // TODO: [SIMULATOR] — 네이티브 실시간 위치 복구 후 이 객체 인자 제거
        isRaceWindow && (meeting as any)?.latitude && (meeting as any)?.longitude
            ? {
                destination: {
                    latitude: Number((meeting as any).latitude),
                    longitude: Number((meeting as any).longitude),
                },
                participants: (membersData?.data || [])
                    .filter((m: any) => m.attendanceStatus === 'ATTENDING')
                    .map((m: any) => Number(m.userNo || m.user_no)),
                excludeUserNos: currentUserNoFromAuth ? [currentUserNoFromAuth] : [],
            }
            : undefined,
    );

    /* 장소 사진·후기를 올릴 수 있는 때인가.
       모임이 시작된 뒤면 진행 중이든 끝난 뒤든 올릴 수 있습니다.
       다녀오고 나서 정리해 올리는 사람이 더 많습니다.
       시작 전에는 막습니다 — 가보지도 않고 남기는 후기가 되니까요. */
    const canAddPlacePhoto = useMemo(() => {
        const start = meetingData?.data?.schedule?.startDatetime;
        if (!start) return false;
        return Date.now() >= new Date(start).getTime();
    }, [meetingData]);

    /* 장소 사진·후기 — 제목 아래 섹션과 장소 시트가 같은 것을 봅니다.
       각자 부르면 두 번 조회되고 좋아요 상태도 따로 놀아서 여기서 한 번만 받습니다. */
    const placeFeed = usePlaceReviews((meeting as any).placeKey || null);
    /* 이 모임에 올라온 기록 요약 — 사진 몇 장과 최근 한 건 */
    const { summary: journalSummary, reload: reloadJournalSummary } = useJournalSummary(meetingId);

    /* 제목 배경 사진.
       외부 제공자 사진(placeInfo/heroPhotos)이 국내 작은 가게에서는 자주 비어
       먹색 면만 남습니다. 그럴 때는 우리 쪽에 모인 사진(가게 사진 → 방문 사진)을
       씁니다. 어차피 같은 장소의 사진이고, 우리가 찍은 것이 더 최근입니다. */
    const heroImages = useMemo(() => Array.from(new Set([
        placeInfo?.photoUrl,
        ...heroPhotos,
        ...feedPhotos(placeFeed),
    ].filter(Boolean) as string[])), [placeInfo?.photoUrl, heroPhotos, placeFeed]);


    const raceDestination = useMemo(() => {
        const lat = (meeting as any)?.latitude;
        const lng = (meeting as any)?.longitude;
        return (lat && lng) ? { latitude: Number(lat), longitude: Number(lng) } : null;
    }, [meeting]);

    const raceParticipants = useMemo(() => {
        return (membersData?.data || [])
            .filter((m: any) => m.attendanceStatus === 'ATTENDING')
            .map((m: any) => {
                let nickname = '';
                const raw = m.nickname || m.nickName || '';
                try { nickname = raw ? decodeURIComponent(raw) : ''; } catch { nickname = raw; }
                return {
                    userNo: Number(m.userNo || m.user_no),
                    nickname: nickname || `유저${m.userNo}`,
                    profileImgUrl: m.profileImgUrl || m.profileImgPath || m.profileUrl,
                    attendanceStatus: m.attendanceStatus,
                };
            });
    }, [membersData]);

    // 사다리타기 모달

    // 정각 피니시 모달 — 약속 시각이 지나고 3초 후 한 번 표시
    const [showFinish, setShowFinish] = useState(false);
    const finishShownRef = useRef(false);
    useEffect(() => {
        finishShownRef.current = false;
    }, [meetingId]);
    useEffect(() => {
        if (!raceEnabled || !meetingStartMs) return;
        if (finishShownRef.current) return;
        const now = Date.now();
        const deltaMs = meetingStartMs - now;
        if (deltaMs < -15 * 60 * 1000) return; // 15분 이상 지났으면 skip
        if (deltaMs <= 0) {
            setShowFinish(true);
            finishShownRef.current = true;
            return;
        }
        const t = setTimeout(() => {
            setShowFinish(true);
            finishShownRef.current = true;
        }, deltaMs + 3000);
        return () => clearTimeout(t);
    }, [raceEnabled, meetingStartMs]);

    // 서버에 저장된 레이스 결과 — finalize 된 모임이면 항상 로드 (영구 카드 표시용)
    const [savedFinishRows, setSavedFinishRows] = useState<RaceFinishRow[] | null>(null);
    const raceFinalizedAt = (meeting as any)?.raceFinalizedAt || null;
    useEffect(() => {
        if (!meetingId || !raceEnabled) return;
        // finalize 되었거나, 피니시 모달 열림 → 결과 조회
        if (!raceFinalizedAt && !showFinish) return;
        (async () => {
            try {
                const res: any = await MeetingRepository.onGetRaceResult(meetingId);
                const list = res?.data?.data;
                if (Array.isArray(list) && list.length > 0) {
                    const profileMap = new Map<number, string | undefined>();
                    (membersData?.data || []).forEach((m: any) => {
                        profileMap.set(Number(m.userNo || m.user_no), m.profileImgUrl || m.profileImgPath || m.profileUrl);
                    });
                    setSavedFinishRows(list.map((r: any) => ({
                        userNo: Number(r.userNo),
                        nickname: r.nickname || `유저${r.userNo}`,
                        rank: Number(r.rankOrder || 0),
                        arrived: Boolean(r.arrived),
                        lateMinutes: Number(r.lateMinutes || 0),
                        isLast: Boolean(r.isLast),
                        excuseReason: r.excuseReason || null,
                        excuseApproved: Boolean(r.excuseApproved),
                        profileImgUrl: profileMap.get(Number(r.userNo)),
                    })));
                }
            } catch (e) {
                console.warn('레이스 결과 조회 실패', e);
            }
        })();
    }, [meetingId, raceEnabled, raceFinalizedAt, showFinish]);

    const finishRows: RaceFinishRow[] = useMemo(() => {
        // 서버 저장 결과 우선
        if (savedFinishRows && savedFinishRows.length > 0) return savedFinishRows;
        if (!showFinish || !raceDestination || !meetingStartMs) return [];
        type RawRow = RaceFinishRow & { _distance: number; _etaMinutes: number | null };
        const rows: RawRow[] = raceParticipants.map((p: { userNo: number; nickname: string; profileImgUrl?: string }): RawRow => {
            const loc = memberLocations[p.userNo];
            if (!loc) {
                return {
                    userNo: p.userNo,
                    nickname: p.nickname,
                    rank: 0,
                    arrived: false,
                    lateMinutes: 0,
                    isLast: false,
                    profileImgUrl: p.profileImgUrl,
                    _distance: Infinity,
                    _etaMinutes: null,
                };
            }
            const eta = calculateEta({
                history: loc.history,
                destination: raceDestination,
                meetingStartMs,
            });
            return {
                userNo: p.userNo,
                nickname: p.nickname,
                rank: 0,
                arrived: eta.arrived,
                lateMinutes: eta.projectedLateMinutes,
                isLast: false,
                profileImgUrl: p.profileImgUrl,
                _distance: eta.distanceMeters,
                _etaMinutes: eta.etaMinutes,
            };
        });
        rows.sort((a: RawRow, b: RawRow) => {
            if (a.arrived && !b.arrived) return -1;
            if (!a.arrived && b.arrived) return 1;
            if (a.arrived && b.arrived) return a._distance - b._distance;
            const aEta = a._etaMinutes ?? 9999;
            const bEta = b._etaMinutes ?? 9999;
            if (aEta !== bEta) return aEta - bEta;
            return a._distance - b._distance;
        });
        rows.forEach((r: RawRow, i: number) => { r.rank = i + 1; });
        const lastIdx = rows.length - 1;
        if (lastIdx > 0 && !rows[lastIdx].arrived) {
            rows[lastIdx].isLast = true;
        }
        return rows.map(({ _distance, _etaMinutes, ...rest }: RawRow) => rest as RaceFinishRow);
    }, [showFinish, raceParticipants, memberLocations, raceDestination, meetingStartMs, savedFinishRows]);

    // finalize 호출 — 피니시 모달이 처음 열릴 때 1회
    //   - 주최자 우선 (3초 지연 없이 즉시)
    //   - 주최자 접속 안 한 경우 대비: 비주최자도 10초 지연 후 호출 (서버 멱등 처리)
    //   - 본인 도착 위치는 캐시 위치가 아닌 그 시점에 새로 측위한 좌표 사용
    //     · 측위 정확도 편차로 도착 판정이 흔들리지 않도록 finalize 직전에 한 번 더 GPS 호출
    //     · 여행 모임 + 호텔 50m 이내면 호텔 좌표로 보정 (호텔에 있으면 호텔이 최종 장소)
    const finalizeCalledRef = useRef(false);
    useEffect(() => {
        if (!showFinish || !meetingId || !raceEnabled) return;
        if (finalizeCalledRef.current) return;
        if (savedFinishRows && savedFinishRows.length > 0) return; // 이미 저장됨
        if (!finishRows || finishRows.length === 0) return;

        const delay = isCreator ? 0 : 10000;
        const timer = setTimeout(async () => {
            if (finalizeCalledRef.current) return;
            finalizeCalledRef.current = true;

            // 본인 위치를 그 시점에 새로 측위 → 본인 row 만 보강
            const myUserNo = currentUserNoFromAuth || Number(sessionStorage.getItem('userNo')) || 0;
            let mySnapshot: { lat: number; lng: number } | null = null;
            if (myUserNo > 0 && raceDestination && meetingStartMs) {
                mySnapshot = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
                    if (!navigator.geolocation) return resolve(null);
                    navigator.geolocation.getCurrentPosition(
                        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                        () => resolve(null),
                        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
                    );
                });

                // 여행 모임 + 호텔 50m 이내 → 호텔 좌표로 보정 (호텔 = 최종 장소)
                if (mySnapshot && isTravelMeeting && hotelCandidatesForFinalize.length > 0) {
                    for (const h of hotelCandidatesForFinalize) {
                        const d = haversineMeters(mySnapshot.lat, mySnapshot.lng, h.lat, h.lng);
                        if (d <= 50) {
                            console.log('[Finalize] 본인 위치 호텔 50m 이내 → 호텔 좌표 사용:', h.name);
                            mySnapshot = { lat: h.lat, lng: h.lng };
                            break;
                        }
                    }
                }
            }

            // 본인 row 만 새 측위 좌표로 arrived/lateMinutes 재계산
            const adjustedRows = mySnapshot && raceDestination && meetingStartMs
                ? finishRows.map(r => {
                    if (Number(r.userNo) !== Number(myUserNo)) return r;
                    const eta = calculateEta({
                        history: [{ latitude: mySnapshot!.lat, longitude: mySnapshot!.lng, timestamp: Date.now() }],
                        destination: raceDestination,
                        meetingStartMs,
                    });
                    console.log('[Finalize] 본인 row 보강:', { before: { arrived: r.arrived, late: r.lateMinutes }, after: { arrived: eta.arrived, late: eta.projectedLateMinutes } });
                    return { ...r, arrived: eta.arrived, lateMinutes: eta.projectedLateMinutes };
                })
                : finishRows;

            const payload = adjustedRows.map(r => ({
                userNo: r.userNo,
                rankOrder: r.rank,
                arrived: r.arrived,
                arrivedAt: r.arrived ? new Date().toISOString() : null,
                distanceMeters: null,
                etaMinutes: null,
                lateMinutes: r.lateMinutes,
                isLast: r.isLast,
            }));
            MeetingRepository.onPostRaceFinalize(meetingId, payload)
                .then(async () => {
                    // finalize 성공 → 서버 결과로 재동기화
                    try {
                        const res: any = await MeetingRepository.onGetRaceResult(meetingId);
                        const list = res?.data?.data;
                        if (Array.isArray(list) && list.length > 0) {
                            setSavedFinishRows(list.map((r: any) => ({
                                userNo: Number(r.userNo),
                                nickname: r.nickname || `유저${r.userNo}`,
                                rank: Number(r.rankOrder || 0),
                                arrived: Boolean(r.arrived),
                                lateMinutes: Number(r.lateMinutes || 0),
                                isLast: Boolean(r.isLast),
                                excuseReason: r.excuseReason || null,
                                excuseApproved: Boolean(r.excuseApproved),
                            })));
                        }
                    } catch { /* 무시 */ }
                })
                .catch((e: any) => {
                    console.warn('레이스 finalize 실패', e);
                    finalizeCalledRef.current = false;
                });
        }, delay);
        return () => clearTimeout(timer);
    }, [showFinish, meetingId, raceEnabled, finishRows, savedFinishRows, isCreator, currentUserNoFromAuth, raceDestination, meetingStartMs, isTravelMeeting, hotelCandidatesForFinalize]);

    // 주최자 여부는 아래 isCreator 로 이미 판정되므로 여기서는 memberLocations / raceTick 을 통해 재렌더만 유도
    void raceTick;

    const handleToggleShuttle = useCallback(async (enabled: boolean) => {
        if (!meetingId) return;
        try {
            await MeetingRepository.onPutRaceSettings(meetingId, { shuttlePenaltyEnabled: enabled });
            meetingStore.onGetMeetingDetail(meetingId);
        } catch (e) {
            showAlert({ message: t('meeting.raceConfigFail') });
        }
    }, [meetingId, showAlert]);

    const handleReportExcuse = useCallback(async (reason: string) => {
        if (!meetingId) return;
        try {
            await MeetingRepository.onPostRaceExcuse(meetingId, reason);
            await showAlert({ message: t('meeting.excuseSubmitted') });
        } catch (e) {
            await showAlert({ message: t('meeting.excuseFail') });
        }
    }, [meetingId, showAlert]);

    const handleApproveExcuse = useCallback(async (targetUserNo: number, approved: boolean) => {
        if (!meetingId) return;
        try {
            await MeetingRepository.onPutRaceExcuseApprove(meetingId, targetUserNo, approved);
            // 승인/거절 후 결과 재조회
            const res: any = await MeetingRepository.onGetRaceResult(meetingId);
            const list = res?.data?.data;
            if (Array.isArray(list)) {
                setSavedFinishRows(list.map((r: any) => ({
                    userNo: Number(r.userNo),
                    nickname: r.nickname || `유저${r.userNo}`,
                    rank: Number(r.rankOrder || 0),
                    arrived: Boolean(r.arrived),
                    lateMinutes: Number(r.lateMinutes || 0),
                    isLast: Boolean(r.isLast),
                    excuseReason: r.excuseReason || null,
                    excuseApproved: Boolean(r.excuseApproved),
                })));
            }
        } catch (e) {
            await showAlert({ message: t('meeting.processFail') });
        }
    }, [meetingId, showAlert]);

    // 1시간 이내 시작하거나 진행 중인 모임인지 확인 (종료 시간까지 배너 노출)
    const upcomingSoonInfo = useMemo(() => {
        if (!meetingData?.data?.schedule?.startDatetime) return null;

        const now = new Date();
        const meetingTime = new Date(meetingData.data.schedule.startDatetime);
        const endTime = meetingData.data.schedule.endDatetime
            ? new Date(meetingData.data.schedule.endDatetime)
            : null;

        // 종료 시간이 지났으면 표시하지 않음
        if (endTime && endTime < now) return null;

        const diffMs = meetingTime.getTime() - now.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        // 이미 시작했지만 아직 종료되지 않은 경우
        if (diffMinutes <= 0) {
            return { timeText: null }; // 진행 중
        }

        // 1시간 이내 시작 예정
        if (diffMinutes <= 60) {
            return {
                timeText: diffMinutes < 60 ? t('meeting.minText', { min: diffMinutes }) : t('meeting.hourText')
            };
        }
        return null;
    }, [meetingData?.data?.schedule?.startDatetime, meetingData?.data?.schedule?.endDatetime]);

    // 시간 디버깅 로그 (필요시에만)
    useEffect(() => {
        if (meetingData?.data?.schedule?.startDatetime) {
            console.log('=== MeetingDetail 시간 표시 디버깅 ===');
            console.log('API에서 받은 startDatetime:', meetingData.data.schedule.startDatetime);
            const startDate = new Date(meetingData.data.schedule.startDatetime);
            console.log('Date 객체:', startDate.toString());
            console.log('toLocaleString:', startDate.toLocaleString('ko-KR'));
            console.log('toLocaleTimeString:', startDate.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }));
        }
    }, [meetingData?.data?.schedule?.startDatetime]);

    // 현재 사용자의 참석 상태 + 위치 공유 여부 확인 및 설정
    useEffect(() => {
        const storedUserNo = sessionStorage.getItem('userNo');
        const currentUserNo = currentUserNoFromAuth || (storedUserNo ? Number(storedUserNo) : null);
        if (!currentUserNo || !membersData?.data) return;
        const currentMember = membersData.data.find((m: any) => Number(m.userNo) === Number(currentUserNo));

        if (currentMember) {
            const status = currentMember.attendanceStatus;
            if (status === 'ATTENDING') setAttendanceStatus('attending');
            else if (status === 'NOT_ATTENDING') setAttendanceStatus('not_attending');
            else setAttendanceStatus('undecided');
            // 위치 공유: 서버 값 우선
            if (typeof currentMember.locationSharing === 'boolean') {
                setIsLocationSharing(currentMember.locationSharing);
                // localStorage도 서버와 sync
                if (meetingId) localStorage.setItem(`locationSharing_${meetingId}`, String(currentMember.locationSharing));
            }
            // 참석 미정이면 자동으로 prompt 띄움 — 모임 생성 직후 [홈으로] 로 빠져나가
            // attendance 결정 안 한 사용자가 모임 상세에 처음 들어오는 시점 cover.
            // (?att=1 / state.showAttendancePrompt 명시 트리거는 그대로 유지)
            if (!status || status === 'UNDECIDED') {
                setShowAttendPrompt(true);
            }
        } else {
            console.warn('[MeetingDetail] 내 멤버 레코드 못 찾음 — userNo:', currentUserNo, 'members:', membersData.data?.map((m: any) => m.userNo));
        }
    }, [membersData, currentUserNoFromAuth, meetingId]);

    const handleEdit = async () => {
        // 주최자가 아닌 멤버면 주최자 동의 여부 확인 팝업 (실제 시스템 검증은 없음, UX용)
        if (!isCreator) {
            const ok = await showConfirm({
                message: t('meeting.editInfoConfirm'),
                confirmText: t('meeting.editInfoConfirmBtn'),
            });
            if (!ok) return;
        }
        // 예전에는 확인 모달이 닫히며 부르던 history.back() 이 직후의 navigate() 를
        // 되돌려 버려 두 프레임을 기다렸다. 이제 모달은 history 를 되돌리지 않는다.
        navigate(`/meeting/edit/${meetingId}`);
    };

    const handleShare = async () => {
        const link = `${window.location.origin}/invitation/${meeting.inviteCode}`;
        const text = t('meeting.inviteText', { name: meeting.name, code: meeting.inviteCode });

        const success = await openShare({
            title: t('meeting.shareTitle', { name: meeting.name }),
            text,
            url: link,
        });

        if (!success) {
            // Web Share API 미지원 시 링크 복사 fallback
            await copyToClipboard(`${text}\n${link}`);
            showAlert({ message: t('meeting.inviteLinkCopied') });
        }
    };

    const handleCopyInviteCode = async () => {
        await copyToClipboard(meeting.inviteCode);
        showAlert({ message: t('meeting.codeCopied') });
    };

    const handleCopyLink = async () => {
        const link = `${window.location.origin}/invitation/${meeting.inviteCode}`;
        await copyToClipboard(link);
        showAlert({ message: t('meeting.linkCopied') });
    };

    const handleChat = () => {
        navigate(`/chat/room/${meetingId}`);
    };

    // 안전 귀가 — 새 세션 시작 페이지로 이동
    const handleSafeReturnStart = () => {
        navigate(`/safe-return/start/${meetingId}`);
    };

    // 서브 장소 추가 모달 열기
    const handleAddSubPlace = () => {
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    };

    // 숙박지 추가 — 카테고리 HOTEL 프리필 + 체크인/체크아웃 디폴트
    // 첫 추가: 첫째 날 15:00 ~ 마지막 날 11:00
    // 추가 등록: 직전 호텔 체크아웃 일자 15:00 ~ 마지막 날 11:00
    const handleAddHotel = () => {
        setEditingSubPlace(null);

        let defaultCheckInDate = '';
        let defaultCheckOutDate = '';
        let defaultCheckInTime = '';
        let defaultCheckOutTime = '';
        if (travelPeriod) {
            const pad = (n: number) => String(n).padStart(2, '0');
            const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            const existingCheckOuts = (subPlaces || [])
                .filter(sp => sp.category === 'HOTEL')
                .map(sp => (sp.endDatetime || '').slice(0, 10))
                .filter((d): d is string => !!d)
                .sort();
            const lastCheckOut = existingCheckOuts.length > 0
                ? existingCheckOuts[existingCheckOuts.length - 1]
                : null;
            defaultCheckInDate = lastCheckOut || fmt(travelPeriod.start);
            defaultCheckOutDate = fmt(travelPeriod.end);
            defaultCheckInTime = '15:00';
            defaultCheckOutTime = '11:00';
        }

        setSubPlaceForm({
            placeName: '',
            placeAddress: '',
            category: 'HOTEL',
            dayNumber: 1,
            dayNumbers: [1],
            visitTime: '',
            description: '',
            checkInDate: defaultCheckInDate,
            checkInTime: defaultCheckInTime,
            checkOutDate: defaultCheckOutDate,
            checkOutTime: defaultCheckOutTime,
        });
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    };

    // 서브 장소 수정 모달 열기
    const handleEditSubPlace = (subPlace: SubPlace) => {
        setEditingSubPlace(subPlace);
        setSubPlaceForm(undefined); // SubPlaceModal will populate from editingSubPlace
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    };

    // 서브 장소 모달 닫기
    const handleCloseSubPlaceModal = () => {
        setShowSubPlaceModal(false);
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
        // location.state를 클리어해서 재오픈 방지
        if (location.state?.fromSubPlaceModal) {
            navigate(location.pathname, { replace: true, state: {} });
        }
    };

    // 서브 장소 삭제
    const handleDeleteSubPlace = async (subPlace: SubPlace) => {
        if (!meetingId) return;

        const confirmed = await showConfirm({
            message: t('meeting.deleteSubPlaceConfirm', { name: subPlace.placeName }),
            confirmText: t('meeting.deleteExpenseBtn')
        });

        if (confirmed) {
            try {
                await MeetingRepository.onDeleteSubPlace(meetingId, subPlace.subPlaceId);
                showAlert({ message: t('meeting.placeDeleted') });
                fetchSubPlaces();
            } catch (error) {
                console.error('서브 장소 삭제 실패:', error);
                showAlert({ message: t('meeting.placeDeleteFailed') });
            }
        }
    };

    // 참석 상태 변경 핸들러
    // 정책: 한 번의 PUT /members/me 로 attendanceStatus + locationSharing 동시 갱신 →
    //  - 백엔드 updateMembership 1회 호출 → push 최대 1번 (이전엔 onPutUpdateAttendance + onPutMyMembership
    //    두 번 호출이 race 면 둘 다 OLD status 조회 → push 2번 발송 회귀)
    //  - in-flight 가드(isUpdatingAttendance)로 더블클릭 방지
    const handleAttendanceChange = async (newStatus: 'attending' | 'not_attending' | 'undecided') => {
        if (attendanceStatus === newStatus || !meetingId) return;
        if (isUpdatingAttendance) return;

        const statusMap = {
            'attending': 'ATTENDING' as const,
            'not_attending': 'NOT_ATTENDING' as const,
            'undecided': 'UNDECIDED' as const
        };

        const statusLabel = {
            'attending': t('meeting.attending'),
            'not_attending': t('meeting.notAttending'),
            'undecided': t('meeting.undecided')
        };

        // 위치공유 결정 — 호출 전에 미리 정해서 단일 PUT 으로 합침
        let nextShare: boolean | undefined;
        if (newStatus === 'attending') {
            // 참석으로 변경 시 위치공유 의사 묻기
            const wantShare = await showConfirm({
                title: t('meeting.attendancePrompt.locationShare'),
                message: t('meeting.attendancePrompt.locShareNotice'),
                confirmText: t('meeting.attendancePrompt.locShareOn'),
                cancelText: t('meeting.attendancePrompt.locShareOff'),
            });
            nextShare = !!wantShare;
        } else if ((newStatus === 'not_attending' || newStatus === 'undecided') && isLocationSharing) {
            // 불참/미정 + 기존에 share ON 인 경우만 OFF 로 갱신 (이미 OFF 면 굳이 안 보냄)
            nextShare = false;
        }

        setIsUpdatingAttendance(true);
        try {
            const params: { attendanceStatus: 'ATTENDING' | 'NOT_ATTENDING' | 'UNDECIDED'; locationSharing?: boolean } = {
                attendanceStatus: statusMap[newStatus],
            };
            if (nextShare !== undefined) params.locationSharing = nextShare;
            const response = await MeetingRepository.onPutMyMembership(meetingId, params);
            const httpStatus = (response as any)?.status;
            const isOk = httpStatus == null || (httpStatus >= 200 && httpStatus < 300);
            if (isOk) {
                setAttendanceStatus(newStatus);
                if (nextShare !== undefined) {
                    setIsLocationSharing(nextShare);
                    localStorage.setItem(`locationSharing_${meetingId}`, String(nextShare));
                }
                showAlert({ message: t('meeting.changedTo', { status: statusLabel[newStatus] }) });
                // 모임 멤버 정보 + 홈 모임 목록 즉시 refetch — 목록 stale 회귀 방지
                meetingStore.onGetMeetingMembers(meetingId);
                meetingStore.onGetMeetingList();

                // 네이티브 schedule 처리
                if (newStatus === 'attending' && nextShare && isNativeApp()) {
                    // 실제 시작 시각 그대로 등록 — 네이티브가 1시간 전부터 알아서 켠다.
                    syncNativeMeetingSchedule(meetingId, meetingData?.data);
                }
                if (nextShare === false && isNativeApp()) removeNativeMeetingSchedule(meetingId);
            } else {
                showAlert({ message: t('meeting.attendanceChangeFailed') });
            }
        } catch (error) {
            console.error('참석 상태 변경 실패:', error);
            showAlert({ message: t('meeting.attendanceChangeError') });
        } finally {
            setIsUpdatingAttendance(false);
        }
    };

    // 내 위치공유 토글 — 서버 업데이트 + 네이티브 브릿지 + localStorage 미러
    const handleLocationSharingToggle = async () => {
        if (!meetingId) return;
        const newVal = !isLocationSharing;
        // 낙관적 UI
        setIsLocationSharing(newVal);
        localStorage.setItem(`locationSharing_${meetingId}`, String(newVal));
        try {
            const res: any = await MeetingRepository.onPutMyMembership(meetingId, { locationSharing: newVal });
            if (res?.status && res.status >= 400) throw new Error('server error');
            // 네이티브 브릿지 동기화
            if (isNativeApp()) {
                if (newVal && attendanceStatus === 'attending') {
                    syncNativeMeetingSchedule(meetingId, meetingData?.data);
                } else {
                    removeNativeMeetingSchedule(meetingId);   // 이 모임만 해제
                }
            }
            meetingStore.onGetMeetingMembers(meetingId);
        } catch (e) {
            // 롤백
            setIsLocationSharing(!newVal);
            localStorage.setItem(`locationSharing_${meetingId}`, String(!newVal));
            showAlert({ message: t('meeting.locShareFail') });
        }
    };

    // 주최자 — 모임 취소/삭제
    // - 본인 외 참여자 중 '참석' 또는 '미정' 이 1명이라도 있으면 삭제 차단
    // - 모두 '불참' 이면 사실상 모임이 무산된 상태이므로 그대로 삭제 허용
    const handleCancelMeeting = async () => {
        if (!meetingId) return;

        const blockingMembers = participants.filter(p => !p.isMe && p.status !== 'declined');
        if (blockingMembers.length > 0) {
            await showAlert({
                message: t('meetingUpcoming.deleteBlocked', { count: blockingMembers.length }),
            });
            return;
        }

        const confirmed = await showConfirm({
            message: t('meeting.deleteMeetingConfirm'),
            confirmText: t('meeting.deleteMeetingBtn'),
        });
        if (!confirmed) return;

        try {
            const response = await MeetingRepository.onDeleteMeeting(meetingId);
            const status = response?.status ?? 0;
            if (status >= 200 && status < 300) {
                showAlert({ message: t('meeting.meetingCancelled') });
                navigate('/home', { replace: true });
            } else if (response?.data?.message) {
                await showAlert({ message: response.data.message });
            } else {
                await showAlert({ message: t('meeting.meetingCancelFail') });
            }
        } catch (error) {
            console.error('모임 취소 실패:', error);
            await showAlert({ message: t('meeting.meetingCancelError') });
        }
    };

    const handleLeave = async () => {
        const confirmed = await showConfirm({
            message: t('meeting.leaveConfirm'),
            confirmText: t('meeting.leaveConfirmBtn')
        });

        if (confirmed && meetingId) {
            try {
                const response = await MeetingRepository.onPostLeaveMeeting(meetingId);

                const status = response?.status ?? 0;
                if (status >= 200 && status < 300) {
                    // 성공 시 알림은 비동기로 띄우고 즉시 홈으로 이동 (await 로 인한 화면 정지 방지)
                    showAlert({ message: t('meeting.leftMeeting') });
                    navigate('/home', { replace: true });
                } else if (status === 400 && response?.data?.message) {
                    // 비즈니스 에러 (예: 모임 생성자는 나갈 수 없음)
                    await showAlert({ message: response.data.message });
                } else {
                    await showAlert({ message: t('meeting.leaveFailed') });
                }
            } catch (error) {
                console.error('모임 나가기 실패:', error);
                await showAlert({ message: t('meeting.leaveError') });
            }
        }
    };

    const getStatusBadge = () => {
        switch (meeting.status) {
            case 'upcoming':
                return { text: t('meeting.status.upcoming'), className: 'upcoming' };
            default:
                return { text: t('meeting.status.upcoming'), className: 'upcoming' };
        }
    };

    const badge = getStatusBadge();

    // 모임 lifecycle 4-state — 진행중 / 일정종료 / 모임종료 / 정산완료.
    //   백엔드 sweep 도 동일 기준 (COALESCE(ended_at, schedule.end_datetime)).
    //   - 정산완료: paymentSettledAt set
    //   - 모임종료: schedule.end_datetime + 3일 경과 OR ended_at set, 정산 미완료
    //   - 일정종료: schedule.end_datetime 지났고 3일 안, 정산/종료 모두 미완료
    //   - 진행중: 그 외
    const lifecycleStatus = (() => {
        if (meeting.isRegularMeeting) return null; // 정기 모임은 별도 흐름
        const pSettled = !!(meeting as any).paymentSettledAt;
        if (pSettled) return { text: t('meeting.statusSettled'), className: 'settled' };
        const endedAt = (meeting as any).endedAt;
        const sEndMs = meeting.scheduleEndDatetime ? new Date(meeting.scheduleEndDatetime).getTime() : null;
        const now = Date.now();
        const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
        const explicitEnded = !!endedAt;
        const scheduleEndedGracePassed = sEndMs != null && sEndMs + THREE_DAYS < now;
        if (explicitEnded || scheduleEndedGracePassed) {
            return { text: t('meeting.statusEnded'), className: 'ended' };
        }
        const scheduleEnded = sEndMs != null && sEndMs < now;
        if (scheduleEnded) {
            return { text: t('meeting.statusScheduleEnded'), className: 'schedule-ended' };
        }
        return { text: t('meeting.statusOngoing'), className: 'ongoing' };
    })();

    // 주최자가 아닌 멤버가 수정한 경우에만 "OO님이 수정" 배지 표시
    const showModifierBadge =
        (meeting as any).lastModifiedBy &&
        (meeting as any).creatorNo &&
        (meeting as any).lastModifiedBy !== (meeting as any).creatorNo &&
        (meeting as any).lastModifiedByNickname;
    /* 장소·시간 변경 이력이 있으면 그걸 보여줍니다 — 누르면 이력 시트가 열립니다.
       이력이 없는 예전 모임은 기존 배지를 그대로 씁니다(과거는 추측하지 않습니다). */
    const lastChange = (meeting as any).lastChangedAt ? {
        by: (meeting as any).lastChangedBy,
        nickname: (meeting as any).lastChangedByNickname,
        at: (meeting as any).lastChangedAt,
        targetType: (meeting as any).lastChangedTargetType,
        action: (meeting as any).lastChangedAction,
        targetLabel: (meeting as any).lastChangedTargetLabel,
    } : null;
    const modifierBadge = lastChange ? (
        <LastChangeRow change={lastChange} onOpen={() => setHistoryOpen(true)} />
    ) : showModifierBadge ? (
        <p className={styles.modifier}><IcEdit size={14} /> {t('meeting.modifiedBy', { who: (meeting as any).lastModifiedByNickname })}</p>
    ) : null;

    useBackDismiss(historyOpen, () => setHistoryOpen(false));
    useBackDismiss(showShareModal, () => setShowShareModal(false));
    useBackDismiss(showMapModal, () => setShowMapModal(false));
    useBackDismiss(showAttendPrompt, () => setShowAttendPrompt(false));

    // 완료 모임 모드 — 종료 60분 후 또는 주최자 명시 종료 시. 정기 모임은 별도 기획이라 제외.
    const FINISHED_GRACE_MS = 60 * 60 * 1000;
    const scheduleEndMs = meeting.scheduleEndDatetime ? new Date(meeting.scheduleEndDatetime).getTime() : null;
    const isFinishedMode = !meeting.isRegularMeeting && (
        !!(meeting as any).endedAt
        || (scheduleEndMs != null && (Date.now() - scheduleEndMs) >= FINISHED_GRACE_MS)
    );

    if (isFinishedMode && meetingId) {
        // hotelPlace — legacy hotelLatitude/Longitude 우선, 없으면 sub_place(category=HOTEL) fallback.
        const finishedHotelPlace = (() => {
            const legacyH = meetingData?.data?.hotelName ? {
                name: meetingData.data.hotelName,
                address: meetingData.data.hotelAddress || '',
                lat: meetingData.data.hotelLatitude ? parseFloat(meetingData.data.hotelLatitude) : 0,
                lng: meetingData.data.hotelLongitude ? parseFloat(meetingData.data.hotelLongitude) : 0,
            } : null;
            if (legacyH && legacyH.lat && legacyH.lng) return legacyH;
            const sp: any = (hotelPlaces || []).find((x: any) => x.latitude && x.longitude);
            if (sp) {
                return {
                    name: sp.placeName || t('meeting.hotelFallback'),
                    address: sp.address || '',
                    lat: Number(sp.latitude),
                    lng: Number(sp.longitude),
                };
            }
            return null;
        })();
        return (
            <MeetingFinishedView
                meeting={meeting}
                placeFeed={placeFeed}
                subPlaces={subPlaces}
                hotelPlace={finishedHotelPlace}
                participants={participants}
                isCreator={isCreator}
                meetingId={meetingId}
                payments={payments}
                onPaymentsChange={fetchPayments}
            />
        );
    }

    // 예정 모임 모드 — 시작 30분 전 이전(=race window 진입 전) 또는 일정 미정.
    // 정기모임도 다음 회차가 확정되어 있고 롤오버 대기 아니면 동일하게 적용.
    const isUpcomingMode = !isFinishedMode
        && !meeting.rolloverPendingAt
        && (!meetingStartMs || (meetingStartMs - Date.now()) > 30 * 60 * 1000);

    if (isUpcomingMode && meetingId) {
        // AttendancePrompt / InviteFromPastMeetings 모달은 부모 state 로 제어되므로
        // 같은 fragment 안에서 함께 렌더해야 onChangeAttendance / onOpenInviteFromPast 가 동작함.
        return (
            <>
                <MeetingUpcomingView
                    meeting={meeting}
                    members={membersData?.data || []}
                    participants={participants}
                    isCreator={isCreator}
                    isMember={isMember}
                    meetingId={meetingId}
                    startMs={meetingStartMs}
                    subPlaces={subPlaces}
                    isJoining={isJoining}
                    onJoinMeeting={handleJoinMeeting}
                    onShare={handleShare}
                    onOpenInviteFromPast={() => setShowInviteFromPast(true)}
                    onEdit={handleEdit}
                    onChangeAttendance={() => setShowAttendPrompt(true)}
                    externallyInvitedUserNos={invitedFromPastUserNos}
                    isLocationSharing={isLocationSharing}
                    onToggleLocationSharing={handleLocationSharingToggle}
                    attendanceStatus={attendanceStatus}
                    travelDestinationCenter={travelDestinationCenter}
                    travelDestinationName={meeting.travelDestination}
                    travelPeriod={travelPeriod}
                    onMeetingChanged={() => {
                        if (!meetingId) return;
                        meetingStore.onGetMeetingDetail(meetingId);
                        fetchSubPlaces();
                    }}
                />
                <AttendancePrompt
                    visible={showAttendPrompt}
                    meetingId={meetingId}
                    meetingName={location.state?.meetingName || meeting.name}
                    meetingDate={meeting.scheduleStartDatetime ? new Date(meeting.scheduleStartDatetime).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' }) : undefined}
                    meetingTime={meeting.scheduleStartDatetime ? new Date(meeting.scheduleStartDatetime).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) : undefined}
                    meetingLocation={meeting.location && meeting.location !== t('meeting.placeNotSet') ? meeting.location : undefined}
                    initialStatus="ATTENDING"
                    onSubmit={async (status, locationSharing) => {
                        if (meetingId) {
                            const res: any = await MeetingRepository.onPutMyMembership(meetingId, { attendanceStatus: status, locationSharing });
                            const ok = res?.status >= 200 && res?.status < 300;
                            if (!ok) {
                                await showAlert({ message: res?.data?.message || `저장 실패 (${res?.status || 'network'})` });
                                return;
                            }
                            localStorage.setItem(`locationSharing_${meetingId}`, String(locationSharing));
                            setIsLocationSharing(locationSharing);
                            if (status === 'ATTENDING') setAttendanceStatus('attending');
                            else if (status === 'NOT_ATTENDING') setAttendanceStatus('not_attending');
                            else setAttendanceStatus('undecided');
                            // refetch await — stale 데이터 덮어쓰기 회귀 회피
                            await meetingStore.onGetMeetingMembers(meetingId);
                            meetingStore.onGetMeetingList();
                        }
                        setShowAttendPrompt(false);
                        navigate(location.pathname, { replace: true, state: {} });
                    }}
                    onDismiss={() => {
                        setShowAttendPrompt(false);
                        navigate(location.pathname, { replace: true, state: {} });
                    }}
                />
                <InviteFromPastMeetings
                    open={showInviteFromPast}
                    onClose={() => setShowInviteFromPast(false)}
                    targetMeetingId={meetingId}
                    currentUserNo={currentUserNoFromAuth || undefined}
                    excludeUserNos={(membersData?.data || []).map((m: any) => Number(m.userNo)).filter(Boolean)}
                    onInviteSent={(sent) => {
                        // 친구초대 섹션의 같은 userNo 행이 즉시 "초대됨" 으로 보이도록 누적
                        setInvitedFromPastUserNos(prev => Array.from(new Set([...prev, ...sent])));
                        setPendingReloadKey(k => k + 1);
                    }}
                />
            </>
        );
    }

    /* 이 화면에서 "지금 눌러야 할 것" 은 하나뿐입니다.
       비멤버 참여 > 곧 시작 > 정기모임 다음 회차 확인 순으로 딱 하나만 검은 판으로 냅니다. */
    const primaryCall = (() => {
        if (membersData?.data && !isMember) {
            const variants = t('joinBanner.inviteVariants', { returnObjects: true }) as unknown;
            const list = Array.isArray(variants) && variants.length > 0
                ? (variants as string[])
                : [t('meeting.detailNotJoined')];
            // meetingId 기준 안정 랜덤 — 같은 모임은 리렌더마다 문구가 바뀌지 않습니다
            const seed = (meetingId || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
            return {
                title: list[seed % list.length],
                sub: isJoining ? t('joinBanner.participating') : t('meeting.detailNotJoined'),
                onClick: isJoining ? undefined : handleJoinMeeting,
            };
        }
        if (upcomingSoonInfo) {
            return {
                title: upcomingSoonInfo.timeText
                    ? t('meeting.meetingSoonBefore', { time: upcomingSoonInfo.timeText })
                    : t('meeting.meetingInProgress'),
                sub: t('meeting.meetingSoonDesc'),
                onClick: () => navigate(`/meeting/location/${meetingId}`),
            };
        }
        if (isCreator && meeting.rolloverPendingAt) {
            return {
                title: t('meeting.rolloverTitle'),
                sub: t('meeting.rolloverSub', { summary: formatRepeatSummary(meeting) }),
                onClick: async () => {
                    if (!meetingId) return;
                    try {
                        await MeetingRepository.onPostConfirmRollover(meetingId);
                        meetingStore.onGetMeetingDetail(meetingId);
                        showAlert({ message: t('meeting.confirmedRollover') });
                    } catch {
                        showAlert({ message: t('meeting.confirmRolloverFail') });
                    }
                },
            };
        }
        return null;
    })();

    /* 검은 판을 못 받은 나머지 알림들 — 줄로 냅니다 */
    const notices: Array<{ key: string; title: string; desc?: string; onClick: () => void }> = [];
    if (primaryCall && isCreator && meeting.rolloverPendingAt && primaryCall.title !== t('meeting.rolloverTitle')) {
        notices.push({
            key: 'rollover',
            title: t('meeting.rolloverTitle'),
            desc: t('meeting.rolloverSub', { summary: formatRepeatSummary(meeting) }),
            onClick: async () => {
                if (!meetingId) return;
                try {
                    await MeetingRepository.onPostConfirmRollover(meetingId);
                    meetingStore.onGetMeetingDetail(meetingId);
                    showAlert({ message: t('meeting.confirmedRollover') });
                } catch {
                    showAlert({ message: t('meeting.confirmRolloverFail') });
                }
            },
        });
    }
    if (!isPastMeeting && (meeting as any).hasActiveProposal) {
        notices.push({
            key: 'vote',
            title: t('meeting.voteInProgress'),
            desc: t('meetingUpcoming.proposalVoteCta', { count: (meeting as any).proposalVoteCount || 0 }),
            onClick: () => navigate(`/schedule/main/${meetingId}`),
        });
    }
    if (!isPastMeeting && !(meeting as any).hasActiveProposal && !meeting.date) {
        notices.push({
            key: 'coordinate',
            title: t('meeting.scheduleCoordinating'),
            desc: t('meeting.scheduleNotDecided'),
            onClick: () => navigate(`/schedule/main/${meetingId}`),
        });
    }
    /* 내 레이스 벌금 — 미정산 + 사유 미승인일 때만 */
    (() => {
        if (!savedFinishRows || !currentUserNoFromAuth) return;
        const me = savedFinishRows.find(r => r.userNo === currentUserNoFromAuth);
        if (!me) return;
        const fee = (meeting as any)?.lateFeeAmount || 0;
        if (me.arrived || me.lateMinutes < 10 || fee <= 0 || me.excuseApproved) return;
        notices.push({
            key: 'late-fee',
            title: t('meeting.lateFeePending', { amount: fee.toLocaleString() }),
            desc: t('meeting.settleNow'),
            onClick: () => setCostModal({ open: true }),
        });
    })();
    /* 지난 모임 — 정산 요약 */
    if (isPastMeeting) {
        const pastBalance = (meeting as any).myBalanceAmount || 0;
        const pastTotal = (meeting as any).totalPaymentAmount || 0;
        const amt = (n: number) => t('meetingFinished.amountWon', { amount: Math.abs(n).toLocaleString('ko-KR') });
        notices.push(pastTotal > 0 ? {
            key: 'settlement',
            title: pastBalance > 0 ? `${amt(pastBalance)} ${t('meeting.youReceive')}`
                : pastBalance < 0 ? `${amt(pastBalance)} ${t('meeting.youPay')}`
                    : t('meeting.settleComplete'),
            desc: t('meeting.costTotalFormat', { amount: pastTotal.toLocaleString('ko-KR'), count: (payments || []).length }),
            onClick: () => setCostModal({ open: true }),
        } : {
            key: 'settlement',
            title: t('meeting.settlementNone'),
            desc: t('meeting.settlementHint'),
            onClick: () => setCostModal({ open: true }),
        });
    }

    /* 장소가 정해졌는지 — 히어로와 아래 정보 카드가 함께 씁니다 */
    const hasMeetingLocation = meeting.location && meeting.location !== t('meeting.placeNotSet');

    /* 상단 사진 배너에 얹을 값들.
       글자만 있던 자리를 장소 사진으로 바꿉니다. 지금 배지 줄에 있던 것은
       하나도 빼지 않고 칩으로 옮깁니다 (사진이 없으면 먹색 면 위에 그대로). */
    const heroTags: { label: string }[] = [
        { label: badge.text },
        { label: isTravelMeeting ? t('meeting.type.travel') : t('meeting.type.day') },
        { label: meeting.isRegularMeeting ? t('meetingType.regular') : t('meetingType.normal') },
        ...(meeting.isRegularMeeting ? [{ label: formatRepeatSummary(meeting) }] : []),
        ...(lifecycleStatus ? [{ label: lifecycleStatus.text }] : []),
        ...(raceEnabled ? [{ label: raceFinalizedAt ? t('race.doneLabel') : t('race.onLabel') }] : []),
    ].filter(tg => !!tg.label);

    const heroWhere = isTravelMeeting && meeting.travelDestination
        ? meeting.travelDestination + (travelPeriod
            ? ` · ${t('meeting.nightsDays', { nights: travelPeriod.nights, days: travelPeriod.days })}`
            : '')
        : (hasMeetingLocation ? meeting.location : t('meeting.placeUndecided'));
    const heroParagraph = `${heroWhere} · ${t('meeting.heroMembers', { n: participants.length })}`;

    const heroTime = meeting.meetingTime || meeting.time;
    const heroDate = meeting.date
        ? `${meeting.date}${heroTime ? ` · ${heroTime}` : ''}`
        : t('meeting.scheduleNotDecided');

    return (
        <AppScreen
            title={t('meeting.detail')}
            bar="sub"
            right={
                <button
                    type="button"
                    className={styles.barBtn}
                    onClick={handleShare}
                    aria-label={t('meeting.shareLabel')}
                >
                    <IcShare size={18} />
                </button>
            }
        >
            {/* 무슨 모임인지 — 장소 사진 위에 제목·요약을 얹습니다.
                사진이 없으면 HeroBanner 가 먹색 면으로 떨어져 글자는 그대로 읽힙니다. */}
            <Bleed>
                <HeroBanner
                    imageUrl={heroImages[0]}
                    photos={heroImages}
                    tags={heroTags}
                    title={meeting.name}
                    paragraph={heroParagraph}
                    date={heroDate}
                />
            </Bleed>

            {/* 모임이 시작된 뒤에는 이 자리가 사진·후기 자리입니다.
               시작 전에는 친구 초대·공유가 앞에 오는 게 맞지만, 시작한 뒤로는
               지금 그 자리에서 할 수 있는 일이 앞에 와야 합니다. */}
            {/* 기록 남기기 — 읽는 것(PlacePhotoPeek)보다 쓰는 자리가 앞에 옵니다.
               모임이 진행 중이면 지금 그 자리에서 할 수 있는 일이 먼저입니다. */}
            {canAddPlacePhoto && (meeting as any).placeKey && meetingId ? (
                <Section spacing="tight">
                    {/* 쓰는 자리와 읽는 자리를 함께 둡니다 — 이 카드가 작성기를
                        품고 있습니다. 예전에는 작성기만 붙여 놓아, 올린 사진과
                        글이 이 화면에서는 아예 보이지 않았습니다. */}
                    <JournalSummaryCard
                        meetingId={meetingId}
                        summary={journalSummary}
                        anchor={{
                            placeId: (meeting as any).placeKey,
                            placeName: meeting.location,
                            label: meeting.location,
                        }}
                        nickname={myNickname}
                        profileUrl={myProfileUrl}
                        onPosted={() => { placeFeed.reload?.(); void reloadJournalSummary(); }}
                    />
                </Section>
            ) : null}

            {canAddPlacePhoto && (meeting as any).placeKey ? (
                <Section spacing="tight">
                    <PlacePhotoPeek
                        placeId={(meeting as any).placeKey}
                        placeName={meeting.location}
                        meetingId={meetingId}
                        canAdd
                        allowWrite
                        feed={placeFeed}
                    />
                </Section>
            ) : null}

            {primaryCall && (
                <Section spacing="tight">
                    <InverseBanner title={primaryCall.title} sub={primaryCall.sub} onClick={primaryCall.onClick} />
                </Section>
            )}

            {modifierBadge && <Section spacing="tight">{modifierBadge}</Section>}
            <ChangeHistorySheet
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                meetingId={meetingId}
            />

            {notices.length > 0 && (
                <Section spacing="tight">
                    <ActionList>
                        {notices.map(n => (
                            <ActionRow key={n.key} title={n.title} desc={n.desc} onClick={n.onClick} />
                        ))}
                    </ActionList>
                </Section>
            )}

            {treats.length > 0 && (
                <TreatCelebrationBanner
                    items={treats.map((tr: any, idx: number) => ({
                        meetingName: meeting.name,
                        paidByNickname: tr.paidByNickname || t('meeting.someone'),
                        storeName: tr.storeName,
                        round: treats.length - idx,
                        onClick: () => setCostModal({ open: true }),
                    }))}
                />
            )}

            {(() => {
                    const attending = participants.filter(p => p.status === 'confirmed').length;
                    const pending = participants.filter(p => p.status === 'pending').length;
                    const declined = participants.filter(p => p.status === 'declined').length;

                    // 모임 정보 카드 — 정보 + 장소 지도 + 추가 장소 목록
                    const hotelPlace = (() => {
                        const legacyH = meetingData?.data?.hotelName ? {
                            name: meetingData.data.hotelName,
                            address: meetingData.data.hotelAddress || '',
                            lat: meetingData.data.hotelLatitude ? parseFloat(meetingData.data.hotelLatitude) : 0,
                            lng: meetingData.data.hotelLongitude ? parseFloat(meetingData.data.hotelLongitude) : 0,
                        } : null;
                        if (legacyH && legacyH.lat && legacyH.lng) return legacyH;
                        // sub_place(category=HOTEL) fallback — closestKey R3(모든 sub 지남 → 호텔) 발동 조건.
                        const sp: any = (hotelPlaces || []).find((x: any) => x.latitude && x.longitude);
                        if (sp) {
                            return {
                                name: sp.placeName || t('meeting.hotelFallback'),
                                address: sp.address || '',
                                lat: Number(sp.latitude),
                                lng: Number(sp.longitude),
                            };
                        }
                        return null;
                    })();

                    // 모든 장소 목록 구성 (primary + subPlaces, HOTEL 제외)
                    // primary 좌표 결정: meeting.lat/lng → 호텔 → 여행지 fallback
                    // (좌표 없는 채로 push 하면 시청 fallback 으로 미니지도가 잘못된 위치로 떨어짐)
                    type PlaceItem = { key: string; label: string; name: string; lat: number; lng: number; time?: string; sortTs?: number };
                    const allPlaces: PlaceItem[] = [];
                    if (hasMeetingLocation) {
                        let pLat: number | null = meeting.latitude || null;
                        let pLng: number | null = meeting.longitude || null;
                        let source = 'meeting.lat/lng';
                        if ((!pLat || !pLng) && geocodedPrimaryCenter) {
                            pLat = geocodedPrimaryCenter.lat;
                            pLng = geocodedPrimaryCenter.lng;
                            source = 'geocode';
                        }
                        if ((!pLat || !pLng) && hotelPlace) {
                            pLat = hotelPlace.lat;
                            pLng = hotelPlace.lng;
                            source = 'hotel';
                        }
                        if ((!pLat || !pLng) && isTravelMeeting && travelDestinationCenter) {
                            pLat = travelDestinationCenter.lat;
                            pLng = travelDestinationCenter.lng;
                            source = 'travelDestination';
                        }
                        if (!pLat || !pLng) {
                            console.warn('[MeetingDetail] primary 좌표 fallback 모두 실패', {
                                location: meeting.location,
                                travelDestination: meeting.travelDestination,
                                rawLat: meeting.latitude,
                                rawLng: meeting.longitude,
                                hotel: hotelPlace,
                                travelDestCenter: travelDestinationCenter,
                                geocoded: geocodedPrimaryCenter,
                            });
                        } else {
                            console.log('[MeetingDetail] primary 좌표:', source, { lat: pLat, lng: pLng });
                        }
                        if (pLat && pLng) {
                            const primaryTs = meetingData?.data?.schedule?.startDatetime
                                ? new Date(meetingData.data.schedule.startDatetime).getTime() : undefined;
                            allPlaces.push({
                                key: 'primary',
                                label: isTravelMeeting ? t('meeting.markerGather') : t('meeting.markerPlace'),
                                name: meeting.location,
                                lat: pLat, lng: pLng,
                                time: meeting.time,
                                sortTs: primaryTs,
                            });
                        }
                    }
                    // 여행 모임 — 호텔도 allPlaces 에 추가 (chip 에서는 표시되지만 별도 hotel 영역은 그대로 유지).
                    // 모든 sub_place 일정이 끝나면 다음 destination 으로 자동 선택됨.
                    if (isTravelMeeting && hotelPlace) {
                        allPlaces.push({
                            key: 'hotel',
                            label: t('meeting.markerHotel'),
                            name: hotelPlace.name,
                            lat: hotelPlace.lat,
                            lng: hotelPlace.lng,
                            time: undefined,
                            sortTs: undefined, // 호텔은 시간 무관 — closestKey 가 별도 분기로 처리
                        });
                    }
                    (subPlaces || []).filter(sp => sp.category !== 'HOTEL' && sp.latitude && sp.longitude).forEach(sp => {
                        // sortTs 계산 — startDatetime 우선, 없으면 (schedule 시작일 + dayNumber-1) + visitTime
                        // dayNumber 를 무시하면 둘째 날 이상의 sub_place 가 첫째 날 시간으로 잘못 계산되어
                        // 과거 일정으로 인식 → primary 가 active 되는 회귀 발생
                        const ts = sp.startDatetime
                            ? new Date(sp.startDatetime).getTime()
                            : (sp.visitTime && meetingData?.data?.schedule?.startDatetime
                                ? (() => {
                                    const d = new Date(meetingData.data.schedule.startDatetime);
                                    const dayOffset = ((sp as any).dayNumber || 1) - 1;
                                    if (dayOffset > 0) d.setDate(d.getDate() + dayOffset);
                                    const m = sp.visitTime!.match(/(\d+):(\d+)/);
                                    if (m) d.setHours(parseInt(m[1]), parseInt(m[2]), 0, 0);
                                    return d.getTime();
                                })()
                                : undefined);
                        allPlaces.push({
                            key: `sp-${sp.subPlaceId}`,
                            label: sp.category || t('meeting.markerPlace'),
                            name: localizePlaceName(sp.placeName, i18n.language),
                            lat: sp.latitude!, lng: sp.longitude!,
                            time: sp.visitTime,
                            sortTs: ts,
                        });
                    });

                    // 현재시간 기준 가장 가까운 장소 — "다음 이동 장소" 우선
                    //   1) primary 가 미래(아직 시작 안 함) → primary
                    //   2) primary 가 시작했고 미래 sub_place 있음 → 가장 가까운 미래 sub_place
                    //   3) 미래 sub_place 없음 + 여행 모임 + 호텔 있음 → 호텔 (귀가/숙소 안내)
                    //   4) 그 외 sub_place 가 있으면 가장 최근 sub_place / 없으면 primary
                    // now 를 분단위 라운딩 — 렌더 간 값 안정화 (30초 refetch 재렌더에도 같은 결과).
                    const now = Math.floor(Date.now() / 60000) * 60000;
                    const closestKey = (() => {
                        if (allPlaces.length === 0) return null;
                        const primary = allPlaces.find(p => p.key === 'primary') || null;
                        const hotelEntry = allPlaces.find(p => p.key === 'hotel') || null;
                        const subs = allPlaces.filter(p => p.key !== 'primary' && p.key !== 'hotel');

                        // primary 미래 → primary 표시
                        if (primary && typeof primary.sortTs === 'number' && primary.sortTs > now) {
                            return primary.key;
                        }

                        // sub_place 중 미래 가장 가까운 — 스케줄 순서 기반.
                        //   각 sub_place 시각이 되면 그 장소로 자동 전환.
                        const subFuture = subs.filter(s => typeof s.sortTs === 'number' && (s.sortTs as number) >= now);
                        if (subFuture.length > 0) {
                            return [...subFuture].sort((a, b) => (a.sortTs as number) - (b.sortTs as number))[0].key;
                        }

                        // 여행 모임 — 미래 sub_place 일정이 끝나면 다음 destination 은 호텔 (숙박지)
                        if (isTravelMeeting && hotelEntry) {
                            return hotelEntry.key;
                        }

                        // primary 가 시작했고 sub_place 가 있으면 sub_place 우선
                        if (subs.length > 0) {
                            const subWithTs = subs.filter(s => typeof s.sortTs === 'number');
                            if (subWithTs.length > 0) {
                                return [...subWithTs].sort((a, b) => (b.sortTs as number) - (a.sortTs as number))[0].key;
                            }
                            return subs[0].key;
                        }

                        // sub_place 없으면 primary 또는 첫 번째
                        return primary?.key || allPlaces[0].key;
                    })();

                    // 선택된 장소 (없으면 closest). ref 로 안정화 — 데이터 refetch 순간 activeKey 가
                    // 잠깐 null/이전 다른 값으로 튀면 지도 provider(kakao <-> google) 오락가락 원인이라,
                    // 이전 유효값을 유지하고 진짜 변경(사용자 chip 클릭·시간 흐름) 시에만 갱신.
                    const rawActiveKey = (selectedPlaceKey && allPlaces.some(p => p.key === selectedPlaceKey))
                        ? selectedPlaceKey
                        : closestKey;
                    if (rawActiveKey && allPlaces.some(p => p.key === rawActiveKey)) {
                        stableActiveKeyRef.current = rawActiveKey;
                    }
                    const activeKey = stableActiveKeyRef.current || rawActiveKey;
                    const activePlace = allPlaces.find(p => p.key === activeKey) || null;
                    const mapLat = activePlace?.lat ?? 37.5665;
                    const mapLng = activePlace?.lng ?? 126.9780;
                    const mapName = activePlace?.name ?? meeting.location;

                    /* 지도를 눌렀을 때.
                       한국 장소는 카카오맵이 보여야 하므로 웹 위치 화면으로 갑니다.
                       네이티브 위임은 해외 전용 — 카카오 타일이 해외 좌표에서 흰 화면이 되기 때문입니다.
                       (RealtimeLocation 안에도 같은 규칙이 있는데 여기서만 빠져 있어,
                        한국 모임인데 네이티브 구글맵이 뜨고 있었습니다) */
                    const openLocationView = () => {
                        if (isNativeApp() && mapLat && mapLng && !isInKorea(mapLat, mapLng)) {
                            openNativeRealtimeLocation({
                                meetingId: String(meetingId),
                                destination: { name: mapName, address: (activePlace as any)?.address || '', lat: mapLat, lng: mapLng },
                            });
                            return;
                        }
                        navigate(`/meeting/location/${meetingId}`, {
                            state: { destinationOverride: { lat: mapLat, lng: mapLng, name: mapName } },
                        });
                    };

                    /* 장소 칩을 고르면 지도가 그 장소로 — 세 군데서 쓰던 querySelector 를 ref 하나로 모았습니다 */
                    const focusPlace = (key: string) => {
                        setSelectedPlaceKey(key);
                        mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    };

                    const InfoCard = (
                        <Section divided key="info">
                            <SectionHeader
                                title={t('meeting.infoCardTitle')}
                                moreLabel={t('meeting.editLabel')}
                                onMore={handleEdit}
                            />

                            {activePlace ? (
                                <>
                                    <div ref={mapRef} className={styles.mapLabel}>
                                        {activePlace.label} · {activePlace.name}
                                        {activeKey === closestKey && allPlaces.length > 1 && (
                                            <TimeBadge size="inline" urgent>{t('meeting.nowLabel')}</TimeBadge>
                                        )}
                                    </div>
                                    <Bleed>
                                        <div
                                            className={styles.map}
                                            role="button"
                                            tabIndex={0}
                                            onClick={openLocationView}
                                            onKeyDown={(e) => { if (e.key === 'Enter') openLocationView(); }}
                                        >
                                            <MapContainer
                                                center={{ lat: mapLat, lng: mapLng }}
                                                markers={(() => {
                                                    const list: any[] = [{
                                                        id: 'active-place',
                                                        lat: mapLat,
                                                        lng: mapLng,
                                                        label: '',
                                                        tooltip: mapName,
                                                        isDestination: true,
                                                    }];
                                                    if (showMyGpsMarker && userGpsLocation) {
                                                        list.push({
                                                            id: 'me',
                                                            lat: userGpsLocation.lat,
                                                            lng: userGpsLocation.lng,
                                                            label: t('meeting.markerMe'),
                                                            tooltip: t('meeting.markerMyLoc'),
                                                        });
                                                    }
                                                    return list;
                                                })()}
                                                height={220}
                                                zoomLevel={7}
                                                // fitBounds 를 켜면 사용자 GPS(예: 서울)와 여행 목적지(예: 후쿠오카)가
                                                // 모두 보이도록 광역으로 줌아웃돼 목적지가 점처럼 보입니다.
                                                // activePlace 를 focusTarget 으로 잡아 그 좌표에만 초점을 유지합니다.
                                                fitBounds={false}
                                                focusTarget={{
                                                    lat: mapLat,
                                                    lng: mapLng,
                                                    level: 7,
                                                    key: `mp-${activeKey || 'primary'}`,
                                                }}
                                                placeName={mapName}
                                                placeAddress={(activePlace as any)?.address || ''}
                                            />
                                        </div>
                                    </Bleed>

                                    {allPlaces.length > 1 && (
                                        <div className={styles.chips}>
                                            {allPlaces.map(p => (
                                                <button
                                                    key={p.key}
                                                    type="button"
                                                    className={activeKey === p.key ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                                    onClick={(e) => { e.stopPropagation(); focusPlace(p.key); }}
                                                >
                                                    <span className={styles.chipLabel}>{p.label}</span>
                                                    <span className={styles.chipName}>{p.name}</span>
                                                    {p.time && <span className={styles.chipTime}>{p.time}</span>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <EmptyState
                                    title={t('meeting.mapEmptyTitle')}
                                    body={t('meeting.mapEmptySub')}
                                    action={<Button onClick={handleEdit}>{t('meeting.mapEmptyCta')}</Button>}
                                />
                            )}

                            {/* 숙소 지도 — 여행 모임만 */}
                            {isTravelMeeting && hotelPlace && (
                                <>
                                    <div className={styles.mapLabel}>{t('meeting.accommodation')}</div>
                                    <Bleed>
                                        <div className={styles.map}>
                                            <MapContainer
                                                center={{ lat: hotelPlace.lat, lng: hotelPlace.lng }}
                                                markers={[{
                                                    id: 'hotel-place',
                                                    lat: hotelPlace.lat,
                                                    lng: hotelPlace.lng,
                                                    label: '',
                                                    tooltip: hotelPlace.name,
                                                    isDestination: true,
                                                }]}
                                                height={200}
                                                zoomLevel={7}
                                            />
                                        </div>
                                    </Bleed>
                                </>
                            )}

                            <InfoGrid
                                items={[
                                    {
                                        label: t('meeting.dateLabelS'),
                                        value: meeting.date || t('meeting.scheduleNotDecided'),
                                        muted: !meeting.date,
                                    },
                                    {
                                        label: t('meeting.timeLabelS'),
                                        value: meeting.meetingTime || meeting.time || t('meeting.placeUndecided'),
                                        muted: !(meeting.meetingTime || meeting.time),
                                    },
                                    ...(isTravelMeeting && hotelPlace ? [
                                        {
                                            label: t('meeting.gatherPlace'),
                                            value: hasMeetingLocation ? meeting.location : t('meeting.placeUndecided'),
                                            muted: !hasMeetingLocation,
                                        },
                                        {
                                            label: t('meeting.accommodation'),
                                            value: hotelPlace.address || hotelPlace.name,
                                        },
                                    ] : [
                                        {
                                            label: isTravelMeeting ? t('meeting.gatherPlace') : t('meeting.markerPlace'),
                                            value: hasMeetingLocation ? meeting.location : t('meeting.placeUndecided'),
                                            muted: !hasMeetingLocation,
                                            full: true,
                                        },
                                    ]),
                                    { label: t('meeting.hostLabelS'), value: meeting.createdBy },
                                    {
                                        label: t('meeting.categoryLabelS'),
                                        value: meetingData?.data?.category && meetingData.data.category !== 'ETC'
                                            ? meetingData.data.category : '—',
                                        muted: !(meetingData?.data?.category && meetingData.data.category !== 'ETC'),
                                    },
                                ]}
                            />

                            {meeting.inviteCode && (
                                <CopyRow
                                    label={t('meeting.inviteCodeLabelS')}
                                    value={meeting.inviteCode}
                                    copyLabel={t('meeting.copyCode')}
                                    onCopy={() => {
                                        copyToClipboard(meeting.inviteCode);
                                        showAlert({ message: t('meeting.codeCopied') });
                                    }}
                                />
                            )}

                            {meeting.description && <p className={styles.desc}>{meeting.description}</p>}
                        </Section>
                    );
                    const statusLabel = (s: Participant['status']) =>
                        s === 'confirmed' ? t('meeting.attending')
                            : s === 'declined' ? t('meeting.notAttending')
                                : t('meeting.undecided');

                    const ParticipantsCard = (
                        <Section divided key="participants">
                            <SectionHeader
                                title={t('meeting.participantsTitle', { n: participants.length })}
                                moreLabel={t('meetingInvite.invite')}
                                onMore={() => setShowInviteFromPast(true)}
                            />

                            {!isMember ? (
                                <>
                                    <p className={styles.hint}>{t('meeting.invitedToMeeting')}</p>
                                    <Button block disabled={isJoining} onClick={handleJoinMeeting}>
                                        {isJoining ? t('meeting.accepting') : t('meeting.acceptInvite')}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {/* 내 참석 여부 — 셋 중 하나 */}
                                    <div className={styles.pick} role="radiogroup" aria-label={t('meeting.attending')}>
                                        {([
                                            { key: 'attending', label: t('meeting.attending') },
                                            { key: 'undecided', label: t('meeting.undecided') },
                                            { key: 'not_attending', label: t('meeting.notAttending') },
                                        ] as const).map(opt => (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                role="radio"
                                                aria-checked={attendanceStatus === opt.key}
                                                className={attendanceStatus === opt.key
                                                    ? `${styles.pickItem} ${styles.pickItemOn}`
                                                    : styles.pickItem}
                                                onClick={() => handleAttendanceChange(opt.key)}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    <SwitchList>
                                        <SwitchRow
                                            label={t('meeting.locShareLabel')}
                                            desc={attendanceStatus !== 'attending' ? t('meeting.locShareOnlyShort') : undefined}
                                            checked={!!isLocationSharing}
                                            disabled={attendanceStatus !== 'attending'}
                                            onChange={handleLocationSharingToggle}
                                        />
                                    </SwitchList>
                                </>
                            )}

                            {/* 응답 현황 — 색이 아니라 형태로 구분 */}
                            <ul className={styles.legend}>
                                <li className={styles.legendItem}>
                                    <span className={`${styles.legendMark} ${styles.markAttend}`} aria-hidden />
                                    {t('meeting.attending')}<strong>{attending}</strong>
                                </li>
                                <li className={styles.legendItem}>
                                    <span className={`${styles.legendMark} ${styles.markUndecided}`} aria-hidden />
                                    {t('meeting.undecided')}<strong>{pending}</strong>
                                </li>
                                <li className={styles.legendItem}>
                                    <span className={`${styles.legendMark} ${styles.markDecline}`} aria-hidden />
                                    {t('meeting.notAttending')}<strong>{declined}</strong>
                                </li>
                            </ul>

                            <ul className={styles.list}>
                                {participants.map(p => (
                                    <li key={p.id} className={styles.row}>
                                        <Avatar
                                            size="sm"
                                            person={{
                                                userNo: p.id,
                                                name: p.name || '?',
                                                attendance: p.status === 'confirmed' ? 'going'
                                                    : p.status === 'declined' ? 'declined' : 'pending',
                                                isHost: !!p.isHost,
                                            }}
                                            statusLabel={statusLabel(p.status)}
                                        />
                                        <span className={styles.rowBody}>
                                            <span className={styles.rowName}>
                                                {p.name}
                                                {p.isHost && <span className={styles.rowTag}>{t('meeting.hostLabelS')}</span>}
                                                {p.isMe && <span className={styles.rowTag}>{t('meeting.meLabel')}</span>}
                                            </span>
                                            <span className={styles.rowMeta}>
                                                {p.locationSharing
                                                    ? t('meeting.locShareOnShort')
                                                    : t('meeting.locShareOffShort')}
                                            </span>
                                        </span>
                                        <span className={
                                            p.status === 'confirmed' ? `${styles.rsvp} ${styles.rsvpAttend}`
                                                : p.status === 'declined' ? `${styles.rsvp} ${styles.rsvpDecline}`
                                                    : `${styles.rsvp} ${styles.rsvpUndecided}`
                                        }>
                                            {statusLabel(p.status)}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            {/* 대기 중인 초대자 + 재촉하기 — 멤버에게만 (비멤버는 자기가 피초대자라 의미 없음) */}
                            {meetingId && isMember && (
                                <PendingInvitesSection meetingId={meetingId} reloadKey={pendingReloadKey} />
                            )}
                            {/* 정기모임 — 지난 회차 이력 */}
                            {meetingId && meeting.isRegularMeeting && (
                                <ScheduleHistorySection meetingId={meetingId} />
                            )}
                        </Section>
                    );

                    /* 친구 초대 — 진행 중에도 언제든 초대할 수 있게 */
                    const InviteCard = meetingId && isMember ? (
                        <Section divided key="invite">
                            <SectionHeader title={t('meetingInvite.friendInvite')} />
                            <MeetingInviteSection
                                meetingId={meetingId}
                                inviteCode={meeting?.inviteCode}
                                onShare={handleShare}
                                onOpenInviteFromPast={() => setShowInviteFromPast(true)}
                                withSectionPadding={false}
                                showHeaders={false}
                                externallyInvitedUserNos={invitedFromPastUserNos}
                            />
                        </Section>
                    ) : null;
                    /* 장소 한 곳의 정산 묶음 — 합계 + 결제 줄 + [정산 등록] */
                    const renderPlaceSettlementBlock = (opt: {
                        roundKey: string;
                        placeLabel: string;
                        placeName: string;
                        placeMeta?: string;
                        inline?: boolean;
                    }) => {
                        const summary = computeRoundSummary(opt.roundKey);
                        const fmt = (n: number) => t('meetingFinished.amountWon', { amount: n.toLocaleString('ko-KR') });
                        return (
                            <div className={opt.inline ? `${styles.round} ${styles.roundInline}` : styles.round}>
                                {!opt.inline && (
                                    <div className={styles.roundHead}>
                                        <Badge tone="outline">{opt.placeLabel}</Badge>
                                        <span className={styles.roundBody}>
                                            <span className={styles.roundName}>{opt.placeName}</span>
                                            {opt.placeMeta && <span className={styles.roundMeta}>{opt.placeMeta}</span>}
                                        </span>
                                    </div>
                                )}

                                <div className={styles.roundSum}>
                                    <span className={styles.roundSumLabel}>
                                        {t('meeting.costTotalFormat', {
                                            amount: summary.total.toLocaleString('ko-KR'),
                                            count: summary.count,
                                        })}
                                    </span>
                                    {summary.myBalance !== 0 ? (
                                        <span className={summary.myBalance > 0
                                            ? styles.roundSumValue
                                            : `${styles.roundSumValue} ${styles.roundSumDue}`}>
                                            {summary.myBalance > 0
                                                ? `${t('meeting.receiveMoney')} ${fmt(Math.abs(summary.myBalance))}`
                                                : `${t('meeting.sendMoney')} ${fmt(Math.abs(summary.myBalance))}`}
                                        </span>
                                    ) : (
                                        <span className={styles.roundSumNone}>—</span>
                                    )}
                                </div>

                                {summary.list.length > 0 && (
                                    <ul className={styles.payList}>
                                        {summary.list.map((p: any) => {
                                            // 블록 머리에 장소명이 이미 있으니 항목 + 메모만 보여 줍니다
                                            const parsed = formatPaymentLine(p.storeName, {
                                                mainPlaceName: meeting.location,
                                                subPlaces,
                                            });
                                            const lineText = parsed.memo
                                                ? `${parsed.cleanName} (${parsed.memo})`
                                                : parsed.cleanName;
                                            return (
                                                <li key={p.paymentId} className={styles.pay}>
                                                    <span className={styles.payWho}>{p.paidByNickname || '?'}</span>
                                                    <span className={styles.payName}>{lineText || t('meeting.expenseFallback')}</span>
                                                    <span className={p.isTreat ? `${styles.payAmount} ${styles.payTreat}` : styles.payAmount}>
                                                        {p.isTreat ? formatPaymentAmount(p) : fmt(Number(p.totalAmount || 0))}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className={styles.payAction}
                                                        onClick={(e) => { e.stopPropagation(); setCostModal({ open: true, paymentId: p.paymentId }); }}
                                                    >
                                                        {t('meeting.editLabel')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`${styles.payAction} ${styles.payActionDanger}`}
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const ok = await showConfirm({
                                                                title: t('meeting.deleteExpenseTitle'),
                                                                message: t('meeting.deleteExpenseMsg'),
                                                                confirmText: t('meeting.deleteExpenseBtn'),
                                                            });
                                                            if (!ok) return;
                                                            try {
                                                                await PaymentRepository.onDeletePayment(p.paymentId);
                                                                showAlert({ message: t('meeting.deletedExpense') });
                                                                fetchPayments();
                                                            } catch {
                                                                showAlert({ message: t('meeting.deleteExpenseFail') });
                                                            }
                                                        }}
                                                    >
                                                        {t('meeting.deleteExpenseBtn')}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                <Button
                                    variant="secondary"
                                    size="sm"
                                    block
                                    onClick={() => setCostModal({ open: true, placeTag: opt.roundKey })}
                                >
                                    {t('meeting.addExpenseTo', { place: opt.placeLabel })}
                                </Button>
                            </div>
                        );
                    };

                    const PlacesCard = (
                        <Section divided key="places">
                            <SectionHeader
                                title={isTravelMeeting ? t('meeting.travelSchedule') : t('meeting.placeAndSettlement')}
                                moreLabel={t('meeting.addPlaceShort')}
                                onMore={handleAddSubPlace}
                            />

                            {isTravelMeeting && (!itinerarySubPlaces || itinerarySubPlaces.length === 0) ? (
                                <EmptyState
                                    title={t('meeting.travelPlaceEmpty')}
                                    body={t('meeting.travelPlaceEmptyHint')}
                                    action={<Button onClick={handleAddSubPlace}>{t('meeting.addPlaceShort')}</Button>}
                                />
                            ) : isTravelMeeting ? (
                                Array.from(new Set(itinerarySubPlaces.map((sp: any) => sp.dayNumber || 1)))
                                    .sort((a: any, b: any) => a - b)
                                    .map((dayNum: number) => (
                                        <div key={dayNum} className={styles.day}>
                                            <div className={styles.dayHead}>
                                                <span className={styles.dayNo}>DAY {dayNum}</span>
                                                {travelPeriod && (
                                                    <span className={styles.dayDate}>
                                                        {new Date(travelPeriod.start.getTime() + (dayNum - 1) * 24 * 60 * 60 * 1000)
                                                            .toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', weekday: 'short' })}
                                                    </span>
                                                )}
                                            </div>
                                            <ul className={styles.list}>
                                                {itinerarySubPlaces
                                                    .filter((sp: any) => (sp.dayNumber || 1) === dayNum)
                                                    .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))
                                                    .map((sp: any) => (
                                                        <li key={sp.subPlaceId} className={styles.row}>
                                                            <span
                                                                className={styles.rowBody}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => focusPlace(`sp-${sp.subPlaceId}`)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') focusPlace(`sp-${sp.subPlaceId}`); }}
                                                            >
                                                                <span className={styles.rowTags}>
                                                                    <Badge tone="outline">{getCategoryLabel(sp.category)}</Badge>
                                                                    {sp.visitTime && <TimeBadge size="inline">{sp.visitTime}</TimeBadge>}
                                                                </span>
                                                                <span className={styles.rowName}>
                                                                    {localizePlaceName(sp.placeName, i18n.language)}
                                                                </span>
                                                            </span>
                                                            <span className={styles.rowActions}>
                                                                <button
                                                                    type="button"
                                                                    className={styles.payAction}
                                                                    onClick={() => handleEditSubPlace(sp)}
                                                                >
                                                                    {t('meeting.editLabel')}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`${styles.payAction} ${styles.payActionDanger}`}
                                                                    onClick={() => handleDeleteSubPlace(sp)}
                                                                >
                                                                    {t('meeting.deleteExpenseBtn')}
                                                                </button>
                                                            </span>
                                                        </li>
                                                    ))}
                                            </ul>
                                        </div>
                                    ))
                            ) : (
                                <>
                                    {/* 본 장소 — 'main' 태그 결제 + 라벨 없는 결제. 장소가 미정이어도 정산은 등록할 수 있게 */}
                                    {renderPlaceSettlementBlock({
                                        roundKey: 'main',
                                        placeLabel: t('meeting.roundOrdinal', { n: 1 }),
                                        placeName: meeting.location || t('meeting.mainPlaceUndecided'),
                                        placeMeta: meeting.locationDetail || meeting.time || '',
                                    })}

                                    {(!itinerarySubPlaces || itinerarySubPlaces.length === 0) && (
                                        <p className={styles.hint}>{t('meeting.addMorePlacesHint')}</p>
                                    )}

                                    {itinerarySubPlaces
                                        .slice()
                                        .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))
                                        .map((sp: any, idx: number) => (
                                            <div key={sp.subPlaceId} className={styles.round}>
                                                <div className={styles.roundHead}>
                                                    <Badge tone="outline">{t('meeting.roundOrdinal', { n: idx + 2 })}</Badge>
                                                    <span
                                                        className={styles.roundBody}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => focusPlace(`sp-${sp.subPlaceId}`)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') focusPlace(`sp-${sp.subPlaceId}`); }}
                                                    >
                                                        <span className={styles.roundName}>
                                                            {localizePlaceName(sp.placeName, i18n.language)}
                                                        </span>
                                                        <span className={styles.roundMeta}>
                                                            {getCategoryLabel(sp.category)}
                                                            {sp.visitTime ? ` · ${sp.visitTime}` : ''}
                                                            {sp.createdByNickname
                                                                ? ` · ${t('meeting.detailV6.pickedBy', { who: sp.createdByNickname })}`
                                                                : ''}
                                                        </span>
                                                    </span>
                                                    <span className={styles.rowActions}>
                                                        <button
                                                            type="button"
                                                            className={styles.payAction}
                                                            onClick={() => handleEditSubPlace(sp)}
                                                        >
                                                            {t('meeting.editLabel')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`${styles.payAction} ${styles.payActionDanger}`}
                                                            onClick={() => handleDeleteSubPlace(sp)}
                                                        >
                                                            {t('meeting.deleteExpenseBtn')}
                                                        </button>
                                                    </span>
                                                </div>
                                                {renderPlaceSettlementBlock({
                                                    roundKey: `sub:${sp.subPlaceId}`,
                                                    placeLabel: t('meeting.roundOrdinal', { n: idx + 2 }),
                                                    placeName: sp.placeName,
                                                    inline: true,
                                                })}
                                            </div>
                                        ))}

                                    {/* 기타 — 장소에 묶이지 않는 지출 */}
                                    {(paymentsByPlace['etc'] || []).length > 0 && renderPlaceSettlementBlock({
                                        roundKey: 'etc',
                                        placeLabel: t('meeting.etcLabel'),
                                        placeName: t('meeting.etcExpense'),
                                        placeMeta: t('meeting.etcExpenseDesc'),
                                    })}
                                </>
                            )}
                        </Section>
                    );
                    /* 숙소를 누르면 지도 앱으로 — 카카오맵은 국내만 지원해서 해외는 네이티브/구글 */
                    const openHotelOnMap = (sp: any) => {
                        const lat = sp.latitude;
                        const lng = sp.longitude;
                        if (!lat || !lng) return;
                        const rawName = sp.placeName || t('meeting.hotelFallback');
                        const name = encodeURIComponent(rawName);
                        const inKorea = isInKorea(lat, lng);
                        if (isNativeApp()) {
                            if (inKorea) {
                                requestApp('OPEN_URL', {
                                    url: `kakaomap://look?p=${lat},${lng}`,
                                    fallbackUrl: `https://map.kakao.com/link/map/${name},${lat},${lng}`,
                                });
                            } else {
                                openNativeMapView({
                                    name: rawName,
                                    address: sp.placeAddress || sp.address || '',
                                    lat,
                                    lng,
                                });
                            }
                            return;
                        }
                        window.open(
                            inKorea
                                ? `https://map.kakao.com/link/map/${name},${lat},${lng}`
                                : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
                            '_blank'
                        );
                    };

                    /* 숙소 — 여행 모임 전용 */
                    const HotelCard = isTravelMeeting ? (
                        <Section divided key="hotels">
                            <SectionHeader
                                title={t('meeting.accommodationTitle')}
                                moreLabel={t('meeting.addHotel')}
                                onMore={handleAddHotel}
                            />
                            {(!hotelPlaces || hotelPlaces.length === 0) ? (
                                <EmptyState
                                    title={t('meeting.hotelEmpty')}
                                    body={t('meeting.hotelEmptyHint')}
                                    action={<Button onClick={handleAddHotel}>{t('meeting.addHotel')}</Button>}
                                />
                            ) : (
                                <ul className={styles.list}>
                                    {hotelPlaces.map((sp: any) => (
                                        <li key={sp.subPlaceId} className={styles.row}>
                                            <span
                                                className={styles.rowBody}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => openHotelOnMap(sp)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') openHotelOnMap(sp); }}
                                            >
                                                <span className={styles.rowName}>
                                                    {localizePlaceName(sp.placeName, i18n.language)}
                                                </span>
                                                {(sp.startDatetime || sp.endDatetime) && (
                                                    <span className={styles.rowMeta}>
                                                        {(sp.startDatetime || '').slice(0, 10)}
                                                        {sp.endDatetime ? ` ~ ${String(sp.endDatetime).slice(0, 10)}` : ''}
                                                    </span>
                                                )}
                                                {/* 누가 등록한 숙소인지 — 여럿이 같이 짜니 물어볼 상대가 필요합니다 */}
                                                {sp.createdByNickname && (
                                                    <span className={styles.rowMeta}>
                                                        {t('meeting.detailV6.pickedBy', { who: sp.createdByNickname })}
                                                    </span>
                                                )}
                                            </span>
                                            <span className={styles.rowActions}>
                                                <button
                                                    type="button"
                                                    className={styles.payAction}
                                                    onClick={() => handleEditSubPlace(sp)}
                                                >
                                                    {t('meeting.editLabel')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`${styles.payAction} ${styles.payActionDanger}`}
                                                    onClick={() => handleDeleteSubPlace(sp)}
                                                >
                                                    {t('meeting.deleteExpenseBtn')}
                                                </button>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>
                    ) : null;

                    /* 채팅 — 종료(주최자 종료 · 자동 종료 · 정산 완료)된 모임은 진입 자체를 막습니다 */
                    const isMeetingClosed = !!(meeting as any).endedAt || !!(meeting as any).paymentSettledAt;
                    const ChatCard = isMeetingClosed ? (
                        <Section divided key="chat-closed">
                            <SectionHeader title={t('meeting.chatClosed')} />
                            <EmptyState
                                title={t('meeting.chatClosedMain')}
                                body={t('meeting.chatClosedHint')}
                            />
                        </Section>
                    ) : (
                        <Section divided key="chat">
                            <SectionHeader
                                title={t('meeting.chatTitle')}
                                moreLabel={t('meeting.openChat')}
                                onMore={handleChat}
                            />
                            <ActionList>
                                <ActionRow
                                    standalone
                                    title={t('meeting.chatMain')}
                                    desc={t('meeting.chatHint')}
                                    onClick={handleChat}
                                />
                            </ActionList>
                        </Section>
                    );

                    /* 안심 귀가 — 진행 중 세션 + 시작 */
                    const SafeReturnCard = (
                        <Section divided key="safe-return">
                            <SectionHeader
                                title={t('meeting.safeReturnTitle')}
                                moreLabel={t('meeting.startBtn')}
                                onMore={handleSafeReturnStart}
                            />
                            {safeReturnSessions.length > 0 ? (
                                <ActionList>
                                    {safeReturnSessions.map((s: any) => {
                                        const started = s.startedAt ? new Date(s.startedAt).getTime() : null;
                                        const mins = started ? Math.max(0, Math.floor((Date.now() - started) / 60000)) : null;
                                        let distText = '';
                                        if (s.lastLat != null && s.lastLng != null && s.destLat != null && s.destLng != null) {
                                            const km = haversineMeters(s.lastLat, s.lastLng, s.destLat, s.destLng) / 1000;
                                            const dist = km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`;
                                            distText = ' ' + t('meeting.distToDest', { dist });
                                        }
                                        return (
                                            <ActionRow
                                                key={s.sessionId}
                                                title={t('meeting.returning', { name: s.travelerNickname })}
                                                desc={(mins != null ? t('meeting.minsElapsed', { mins }) : t('meeting.inProgress')) + distText}
                                                onClick={() => navigate(`/safe-return/session/${s.sessionId}`)}
                                            />
                                        );
                                    })}
                                </ActionList>
                            ) : (
                                <ActionList>
                                    <ActionRow
                                        standalone
                                        title={t('meeting.safeReturnMain')}
                                        desc={t('meeting.safeReturnHint')}
                                        onClick={handleSafeReturnStart}
                                    />
                                </ActionList>
                            )}
                        </Section>
                    );
                    // 미니게임 카드 — 사다리 + 병돌리기 (공용 섹션)
                    const miniGameParticipants = raceParticipants.length > 0
                        ? raceParticipants.map((p: any) => ({ userNo: p.userNo, nickname: p.nickname }))
                        : (membersData?.data || [])
                            .filter((m: any) => m.attendanceStatus !== 'NOT_ATTENDING')
                            .map((m: any) => {
                                let nickname = m.nickname || '';
                                try { nickname = decodeURIComponent(nickname); } catch { /* noop */ }
                                return { userNo: Number(m.userNo || m.user_no), nickname: nickname || `유저${m.userNo}` };
                            });
                    const MiniGameCard = (
                        <Section divided key="minigame">
                            <SectionHeader title={t('meeting.miniGameTitle')} />
                            <MiniGamesSection showTitle={false} compact initialParticipants={miniGameParticipants} />
                        </Section>
                    );
                    // 레이스 결과 카드 — finalize 되었거나 저장된 결과가 있을 때만
                    const RaceCard = (savedFinishRows && savedFinishRows.length > 0) ? (
                        <RaceResultCard
                            key="race-result"
                            rows={savedFinishRows}
                            lateFeeAmount={(meeting as any)?.lateFeeAmount || 0}
                            shuttlePenaltyEnabled={(meeting as any)?.shuttlePenaltyEnabled !== false}
                            currentUserNo={currentUserNoFromAuth || null}
                            isHost={isCreator}
                            onOpenDetail={() => setShowFinish(true)}
                            onApproveExcuse={handleApproveExcuse}
                        />
                    ) : null;

                    // 정산은 PlacesCard 내부 각 장소 블록 + 기타 섹션에 통합돼 SettlementCard 제거.
                    // (이전 회귀: 일정/정산이 별도 카드로 분리돼 같은 정보가 두 군데 노출 + 라운드별 그룹핑 부재)

                    // 귀가 도우미 노출 — 여행 모임은 "마지막날 + 내 국가 귀국" 확인 시에만.
                    //   여행 중(해외)엔 의미 없으므로 숨김. 단 안전 기능이라 다음 경우엔 fail-open 노출:
                    //   진행 중 세션 보유 / 여행 종료일 불명 / GPS 불명 / 한국 외 기기(국가 판정 미지원).
                    const safeReturnVisible = (() => {
                        if (!isTravelMeeting) return true;
                        if (safeReturnSessions.length > 0) return true;
                        // 마지막날(여행 종료일) 이후인가
                        const endStr = (meeting as any).travelEndDate;
                        if (endStr) {
                            const end = new Date(endStr);
                            if (!isNaN(end.getTime())) {
                                const today = new Date(); today.setHours(0, 0, 0, 0);
                                end.setHours(0, 0, 0, 0);
                                if (today.getTime() < end.getTime()) return false; // 아직 마지막날 전
                            }
                        }
                        // 내 국가로 귀국했는가 — 기기 국가 KR 기준 한국 안일 때만. GPS 불명이면 fail-open.
                        const deviceCountry = (window as any).__DEVICE__?.countryCd || 'KR';
                        if (deviceCountry === 'KR' && userGpsLocation) {
                            return isInKorea(userGpsLocation.lat, userGpsLocation.lng);
                        }
                        return true;
                    })();
                    const placeCards = HotelCard ? [PlacesCard, HotelCard] : [PlacesCard];
                    // 진행 중 모임에도 친구 초대 노출 — 임박·진행 중에도 추가 초대 가능.
                    // 지난 모임에서는 초대가 의미 없으니 제외.
                    const inviteCards = !isPastMeeting && InviteCard ? [InviteCard] : [];

                    const baseCards = isPastMeeting
                        ? [InfoCard, ParticipantsCard, ...placeCards, ChatCard, ...(safeReturnVisible ? [SafeReturnCard] : []), MiniGameCard]
                        : [InfoCard, ...inviteCards, ParticipantsCard, ...placeCards, ChatCard, ...(safeReturnVisible ? [SafeReturnCard] : []), MiniGameCard];
                    // 레이스 결과는 맨 위(진행형 알림감), 지난 모임이면 모임 정보 다음
                    const orderedCards = RaceCard
                        ? (isPastMeeting
                            ? [baseCards[0], RaceCard, ...baseCards.slice(1)]
                            : [RaceCard, ...baseCards])
                        : baseCards;

                    return (
                        <>
                            {orderedCards}

                            {/* 시간 연장 / 모임 다시 열기 — 주최자, 정기 모임 외, 정산 미완료, 확정 일정 보유.
                                · 모임이 길어지면 종료 시각을 미뤄 위치 추적·지출 등록 흐름을 유지합니다.
                                · 이미 종료된 상태(ended_at set)에서도 노출 — 연장하면 백엔드가 ended_at 을
                                  자동으로 지워 '진행중' 으로 되돌립니다 (ScheduleService.clearEndedAt). */}
                            {isCreator && !meeting.isRegularMeeting && !(meeting as any).paymentSettledAt && (meeting as any).confirmedScheduleId && meeting.scheduleEndDatetime && (
                                <Section divided>
                                    <SectionHeader
                                        title={(meeting as any).endedAt ? t('meeting.reopenMeeting') : t('meeting.extendTime')}
                                        sub={
                                            <>
                                                {(meeting as any).endedAt
                                                    ? t('meeting.reopenDesc')
                                                    : t('meeting.extendDescOngoing')}
                                                <br />
                                                {t('meeting.currentEnd', {
                                                    time: new Date(meeting.scheduleEndDatetime).toLocaleString(i18n.language, {
                                                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                                    }),
                                                })}
                                            </>
                                        }
                                    />
                                    <div className={styles.extend}>
                                        {[30, 60, 120].map(mins => (
                                            <Button
                                                key={mins}
                                                variant="secondary"
                                                size="sm"
                                                onClick={async () => {
                                                    const scheduleId = (meeting as any).confirmedScheduleId;
                                                    const startStr = meeting.scheduleStartDatetime;
                                                    const endStr = meeting.scheduleEndDatetime;
                                                    if (!scheduleId || !startStr || !endStr) return;
                                                    const newEnd = new Date(new Date(endStr).getTime() + mins * 60 * 1000);
                                                    const ok = await showConfirm({
                                                        title: t('meeting.extendTitle'),
                                                        message: t('meeting.extendMsg', {
                                                            time: newEnd.toLocaleString(i18n.language, {
                                                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                                            }),
                                                        }),
                                                        confirmText: t('meeting.extendBtn'),
                                                    });
                                                    if (!ok || !meetingId) return;
                                                    try {
                                                        // 백엔드가 LocalDateTime 을 받으므로 'Z' 없이 로컬 ISO 로 보냅니다
                                                        // 서버는 시각을 UTC 로 받습니다
                                                        const toLocalIso = (d: Date) => toUtcISOString(d);
                                                        await ScheduleRepository.onPutSchedule(String(scheduleId), {
                                                            startDatetime: toLocalIso(new Date(startStr)),
                                                            endDatetime: toLocalIso(newEnd),
                                                        });
                                                        await showAlert({
                                                            message: t('meeting.extendedBy', {
                                                                span: mins >= 60
                                                                    ? t('meeting.plusHours', { h: mins / 60 })
                                                                    : t('meeting.plusMins', { m: mins }),
                                                            }),
                                                        });
                                                        if (meetingData) await meetingStore.onGetMeetingDetail(meetingId);
                                                    } catch (e: any) {
                                                        await showAlert({ message: e?.response?.data?.message || t('meeting.extendFail') });
                                                    }
                                                }}
                                            >
                                                {mins >= 60 ? t('meeting.plusHours', { h: mins / 60 }) : t('meeting.plusMins', { m: mins })}
                                            </Button>
                                        ))}
                                    </div>
                                </Section>
                            )}

                            {/* 모임 종료 — 주최자만, 정기 모임 외, 아직 종료 안 된 상태 */}
                            {isCreator && !meeting.isRegularMeeting && !(meeting as any).endedAt && (
                                <Section divided>
                                    <SectionHeader
                                        title={t('meeting.endMeetingTitle')}
                                        sub={t('meeting.endDesc')}
                                    />
                                    <Button
                                        variant="secondary"
                                        block
                                        onClick={async () => {
                                            const ok = await showConfirm({
                                                title: t('meeting.endTitle'),
                                                message: t('meeting.endMsg'),
                                                confirmText: t('meeting.endBtn'),
                                            });
                                            if (!ok || !meetingId) return;
                                            try {
                                                await MeetingRepository.onPostEndMeeting(meetingId);
                                                showAlert({ message: t('meeting.ended') });
                                                if (meetingData) await meetingStore.onGetMeetingDetail(meetingId);
                                            } catch (e) {
                                                console.error('모임 종료 실패:', e);
                                                showAlert({ message: t('meeting.endFail') });
                                            }
                                        }}
                                    >
                                        {t('meeting.endBtn')}
                                    </Button>
                                </Section>
                            )}

                            {/* 되돌릴 수 없는 것 — 화면 맨 아래 한 곳에 */}
                            {(() => {
                                const aloneAsCreator = isCreator && participants.length <= 1;
                                const creatorWithOthers = isCreator && participants.length > 1;
                                return (
                                    <Section divided last>
                                        <DangerZone
                                            title={isCreator ? t('meeting.delete') : t('meeting.leaveMeetingTitle')}
                                        >
                                            <p className={styles.hint}>
                                                {aloneAsCreator
                                                    ? t('meeting.deleteFully')
                                                    : creatorWithOthers
                                                        ? t('meeting.cantDeleteOthers')
                                                        : t('meeting.leaveThis')}
                                            </p>
                                            <Button
                                                variant="danger"
                                                block
                                                disabled={creatorWithOthers}
                                                onClick={aloneAsCreator ? handleCancelMeeting : handleLeave}
                                            >
                                                {creatorWithOthers
                                                    ? t('meeting.cantDelete')
                                                    : aloneAsCreator
                                                        ? t('meeting.deleteMeetingBtn')
                                                        : t('meetingUpcoming.leaveBtn')}
                                            </Button>
                                        </DangerZone>
                                    </Section>
                                );
                            })()}
                        </>
                    );
                })()}

            {/* 공유 시트 */}
            <Sheet
                open={showShareModal}
                title={t('meeting.shareMeeting')}
                closeLabel={t('common.close')}
                onClose={() => setShowShareModal(false)}
            >
                {meeting.inviteCode && (
                    <CopyRow
                        label={t('meeting.inviteCode')}
                        value={meeting.inviteCode}
                        copyLabel={t('meeting.copyCode')}
                        onCopy={handleCopyInviteCode}
                    />
                )}
                <ActionList>
                    <ActionRow
                        title={t('meeting.shareBtn')}
                        onClick={() => { setShowShareModal(false); handleShare(); }}
                    />
                    <ActionRow title={t('meeting.copyLink')} onClick={handleCopyLink} noArrow />
                    <ActionRow
                        title={t('meeting.sharePastInvite')}
                        desc={t('meeting.sharePastInviteSub')}
                        onClick={() => { setShowShareModal(false); setShowInviteFromPast(true); }}
                    />
                </ActionList>
            </Sheet>

            {/* 장소 상세 시트 — 사진 · 별점 · 지도 · 지도앱으로 열기 */}
            <Sheet
                open={showMapModal}
                title={meeting.location}
                closeLabel={t('common.close')}
                onClose={() => setShowMapModal(false)}
            >
                {placeInfo?.photoUrl && (
                    <img className={styles.placePhoto} src={placeInfo.photoUrl} alt="" />
                )}
                {meeting.locationDetail && <p className={styles.placeAddr}>{meeting.locationDetail}</p>}
                {/* 평점은 우리 것이 먼저입니다. 우리 후기가 있으면 외부 평점은 감춥니다
                   — 두 숫자가 나란히 있으면 어느 쪽을 믿어야 할지 알 수 없습니다.
                   우리 평점은 바로 아래 PlacePhotoPeek 이 보여 줍니다. */}
                {((placeInfo?.rating && !placeFeed.rating) || placeInfo?.openNow !== undefined) && (
                    <div className={styles.tags}>
                        {placeInfo?.rating && !placeFeed.rating ? (
                            <Badge tone="outline">
                                {placeInfo.rating.toFixed(1)}
                                {placeInfo.userRatingsTotal
                                    ? ` · ${t('meeting.reviewCount', { count: placeInfo.userRatingsTotal })}`
                                    : ''}
                            </Badge>
                        ) : null}
                        {placeInfo?.openNow !== undefined && (
                            <Badge tone={placeInfo.openNow ? 'weak' : 'neutral'}>
                                {placeInfo.openNow ? t('meeting.openNow') : t('meeting.closedNow')}
                            </Badge>
                        )}
                    </div>
                )}
                {/* 우리끼리 남긴 사진과 후기 — 외부 평점 아래에 붙습니다 */}
                {(meeting as any).placeKey ? (
                    <PlacePhotoPeek
                        placeId={(meeting as any).placeKey}
                        placeName={meeting.location}
                        meetingId={meetingId}
                        canAdd={canAddPlacePhoto}
                        feed={placeFeed}
                    />
                ) : null}

                <Bleed>
                    <div className={styles.map}>
                        <MapContainer
                            center={{ lat: meeting.latitude || 37.5665, lng: meeting.longitude || 126.9780 }}
                            markers={[{
                                id: 'meeting-place',
                                lat: meeting.latitude || 37.5665,
                                lng: meeting.longitude || 126.9780,
                                label: '',
                                tooltip: meeting.location,
                                isDestination: true,
                            }]}
                            height={260}
                            zoomLevel={2}
                        />
                    </div>
                </Bleed>
                <div className={styles.extend}>
                    {/* 카카오맵은 국내만 보여줍니다 — 해외 장소는 구글로 보냅니다.
                       숙소 카드(openHotelOnMap)는 이미 이렇게 갈라져 있었는데
                       여기만 무조건 카카오여서 해외 여행 모임에서 빈 지도가 떴습니다. */}
                    <Button
                        variant="secondary"
                        icon={<img src={isKoreaPlace ? kakaoMapIcon : googleMapIcon} alt="" className={styles.mapIcon} />}
                        onClick={() => {
                            const lat = meeting.latitude || 37.5665;
                            const lng = meeting.longitude || 126.978;
                            const name = encodeURIComponent(meeting.location);
                            const kakaoWeb = `https://map.kakao.com/link/map/${name},${lat},${lng}`;
                            const googleWeb = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
                            if (isNativeApp()) {
                                if (isKoreaPlace) {
                                    requestApp('OPEN_URL', { url: `kakaomap://look?p=${lat},${lng}`, fallbackUrl: kakaoWeb });
                                } else {
                                    openNativeMapView({
                                        name: meeting.location,
                                        address: meeting.locationDetail || '',
                                        lat, lng,
                                    });
                                }
                            } else {
                                window.open(isKoreaPlace ? kakaoWeb : googleWeb, '_blank');
                            }
                        }}
                    >
                        {isKoreaPlace ? t('meeting.kakaoMap') : t('meeting.googleMap')}
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={() => { setShowMapModal(false); setShowExternalMap(true); }}
                    >
                        {t('meeting.otherMapApps')}
                    </Button>
                </div>
            </Sheet>

            {/* 장소 추가/수정 */}
            <SubPlaceModal
                isOpen={showSubPlaceModal}
                onClose={handleCloseSubPlaceModal}
                meetingId={meetingId}
                editingSubPlace={editingSubPlace}
                isTravelMeeting={isTravelMeeting}
                travelPeriod={travelPeriod}
                meetingTime={isTravelMeeting ? meeting.meetingTime : meeting.time}
                subPlaces={subPlaces}
                onSaveSuccess={fetchSubPlaces}
                returnTo={`/meeting/detail/${meetingId}`}
                initialForm={subPlaceForm}
                initialIsCustomVisitTime={isCustomVisitTime}
                travelDestinationName={meeting.travelDestination}
                travelDestinationCenter={travelDestinationCenter}
            />

            <ExternalMapSheet
                visible={showExternalMap}
                placeName={meeting.location}
                address={meeting.locationDetail}
                lat={meeting.latitude || 37.5665}
                lng={meeting.longitude || 126.978}
                onClose={() => setShowExternalMap(false)}
            />

            <AttendancePrompt
                visible={showAttendPrompt}
                meetingId={meetingId}
                meetingName={location.state?.meetingName || meeting.name}
                initialStatus="ATTENDING"
                onSubmit={async (status, locationSharing) => {
                    if (meetingId) {
                        const res: any = await MeetingRepository.onPutMyMembership(meetingId, { attendanceStatus: status, locationSharing });
                        const ok = res?.status >= 200 && res?.status < 300;
                        if (!ok) {
                            await showAlert({ message: res?.data?.message || t('meeting.saveFail', { status: res?.status || 'network' }) });
                            return;
                        }
                        localStorage.setItem(`locationSharing_${meetingId}`, String(locationSharing));
                        setIsLocationSharing(locationSharing);
                        if (status === 'ATTENDING') setAttendanceStatus('attending');
                        else if (status === 'NOT_ATTENDING') setAttendanceStatus('not_attending');
                        else setAttendanceStatus('undecided');
                        // refetch 를 기다립니다 — useEffect 가 stale 데이터로 덮어쓰지 않도록
                        await meetingStore.onGetMeetingMembers(meetingId);
                        meetingStore.onGetMeetingList();
                    }
                    setShowAttendPrompt(false);
                    navigate(location.pathname, { replace: true, state: {} });
                }}
                onDismiss={() => {
                    setShowAttendPrompt(false);
                    navigate(location.pathname, { replace: true, state: {} });
                }}
            />

            {/* 이전 모임 멤버 초대 */}
            {meetingId && (
                <InviteFromPastMeetings
                    open={showInviteFromPast}
                    onClose={() => setShowInviteFromPast(false)}
                    targetMeetingId={meetingId}
                    currentUserNo={currentUserNoFromAuth || undefined}
                    excludeUserNos={(membersData?.data || []).map((m: any) => Number(m.userNo)).filter(Boolean)}
                    onInviteSent={(sent) => {
                        // 친구초대 섹션의 같은 userNo 행이 즉시 "초대됨" 으로 보이도록 누적
                        setInvitedFromPastUserNos(prev => Array.from(new Set([...prev, ...sent])));
                        setPendingReloadKey(k => k + 1);
                    }}
                />
            )}

            {/* 도착 레이스 — 오른쪽 아래 떠 있는 작은 패널 (참여자 시트 높이만큼 위로) */}
            {isRaceWindow && raceDestination && meetingStartMs && (
                <RaceBottomSheet
                    participants={raceParticipants}
                    memberLocations={memberLocations}
                    destination={raceDestination}
                    meetingStartMs={meetingStartMs}
                    currentUserNo={currentUserNoFromAuth || null}
                    lateFeeAmount={(meeting as any)?.lateFeeAmount || 0}
                    shuttlePenaltyEnabled={(meeting as any)?.shuttlePenaltyEnabled !== false}
                    isHost={isCreator}
                    onToggleShuttle={handleToggleShuttle}
                    onReportExcuse={handleReportExcuse}
                    bottomOffset={participantSheetHeight + 12}
                />
            )}

            {/* 참여자 하단 시트 — 레이스 시간대에만 */}
            {isRaceWindow && participants.length > 0 && (
                <ParticipantMapSheet
                    participants={participants.map(p => ({
                        userNo: Number(p.id),
                        nickname: p.name,
                        attendanceStatus:
                            p.status === 'confirmed' ? 'ATTENDING'
                                : p.status === 'declined' ? 'NOT_ATTENDING' : 'UNDECIDED',
                        locationSharing: !!p.locationSharing,
                        isHost: !!p.isHost,
                        isMe: !!p.isMe,
                    }))}
                    onHeightChange={setParticipantSheetHeight}
                />
            )}

            {/* 정각 피니시 */}
            <RaceFinishModal
                open={showFinish && raceEnabled}
                rows={finishRows}
                currentUserNo={currentUserNoFromAuth || null}
                lateFeeAmount={(meeting as any)?.lateFeeAmount || 0}
                shuttlePenaltyEnabled={(meeting as any)?.shuttlePenaltyEnabled !== false}
                isHost={isCreator}
                onApproveExcuse={handleApproveExcuse}
                onClose={() => setShowFinish(false)}
            />

            {/* 정산 등록/수정 — 화면 이동 없이 어디서든 */}
            {costModal.open && meetingId && (
                <CostInput
                    inModal
                    meetingIdProp={meetingId}
                    paymentIdProp={costModal.paymentId}
                    placeTagProp={costModal.placeTag}
                    onClose={() => setCostModal({ open: false })}
                    onSaved={() => fetchPayments()}
                />
            )}
        </AppScreen>
    );
};
